import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { View, Text, TextInput, Pressable, ScrollView, ActivityIndicator, Image, Modal, StyleSheet, Platform, useWindowDimensions, type ImageSourcePropType, type PressableProps, type StyleProp, type ViewStyle } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import type { ThreadMessage } from '../../services/chat/chatTypes';
import type { ComposerUpload, ComposerAttachment } from '../../services/chat/streamUpload';
import type { SendResult } from '../../services/chat/sendFailure';
import {
  attachmentSignature, attemptDelivered, FILES_NOT_KEPT, isAttemptFile, reopenStep, retryKeyFor,
  type ChatSendAttempt, type SendContent,
} from '../../services/chat/sendAttempt';
import { spacing, radius } from '../../styles';
import { BRAND_REACTIONS, groupByCanonicalReaction, ownReactionSlugs } from '../../constants/brandReactions';
import { useRewardReactions } from '../../hooks/useRewardReactions';
import { RewardLock } from './RewardLock';
import { ReactionGlyph } from './ReactionGlyph';
import { EmojiStrip } from './EmojiStrip';
import { EmojiText } from './EmojiText';
import { EmojiOnlyMessage } from './EmojiOnlyMessage';
import { insertEmojiToken } from '../../utils/emojiTokens';
import { brandifyEmoji } from '../../constants/emojiAliases';
import { EMOJI_ONLY_MAX, emojiOnlyTokens } from '../../utils/emojiOnlyMessage';
import { uiFont } from '../../styles/agapellaFonts';
import { homeChatAppearance } from './homeChatAppearance';
import { useTheme, type ThemeColors } from '../../context/ThemeContext';
import { useReport } from '../../context/ReportContext';
import { HighlightedText } from './HighlightedText';
import { RecommendationCards } from './RecommendationCards';
import { ReflectionOfferButton } from './ReflectionOfferButton';
import { AgapellaSubscriptionOfferButton } from './AgapellaSubscriptionOfferButton';
import { selectTurnQuickReplies } from './quickReplyVisibility';
import { ChatPracticePreview } from './ChatPracticePreview';
import { extractPracticeIds } from '../../utils/chatLinkPreview';
import { renderTextWithLinks } from '../../utils/linkifyMessage';
import { refusalText } from './uploadRefusalText';
import { pickAvatarUrl } from '../../utils/avatarPreview';
import { LevelBadge } from '../gamification/LevelBadge';
import { useLevelBadgesState } from '../gamification/useLevelBadges';
import type { LevelBadgeLoadStatus } from '../gamification/levelBadgeContract';
import type { LevelBadge as LevelBadgeData } from '../../types/auth';
import { replyPreviewSource } from '../../utils/replyPreview';
import { quoteLine } from '../../utils/replyFragment';
import { FragmentAskButton } from './FragmentAskButton';
import { attachmentCaption } from '../../services/chat/communityAttachments';
import { downloadCommunityAttachment, useCommunityAttachmentImage } from '../../services/chat/communityAttachmentFile';
import { mentionsToPlainText, parseMentions, type MentionCandidate } from '../../utils/chatMentions';
import { isProfileUserId } from '../../utils/communityChatMembers';
import { useMentionDraft } from '../../hooks/useMentionDraft';
import { isMobileWebLayout } from '../../widget/widgetPosition';
import { ComposerField } from './ComposerField';
import { DraftField, type DraftFieldHandle } from './DraftField';
import { composerUsesDraftField, voiceUploadMime } from './composerRules';
import { makeMobileComposerStyles, MOBILE_INPUT_MAX_HEIGHT, MOBILE_INPUT_MIN_HEIGHT } from './composerMobileStyles';
import { useComposerAutoGrow } from './useComposerAutoGrow';
import { VoiceMessagePlayer } from './VoiceMessagePlayer';
import { messageMenuMobileStyles } from './messageMenuMobileStyles';
import { MessageMenuLayer, type MenuAnchor } from './MessageMenuLayer';
import { useMessagesSurface, makeHitStyles, type HitStyles } from './messagesSurface';
import { useMessagesV3, ContourRing, type MessagesV3 } from './MessagesV3Parts';
import { MessagesIcon } from './MessagesIcon';
import { makeV3ThreadStyles, overlayStyles, V3_INLINE_EMOJI_STYLE } from './messagesV3Thread';
import { CONTOUR_OPACITY, SEND_GLYPH, V3_EMOJI_INLINE, initialsOf } from './messagesV3';
import { useI18n } from '../../i18n';

/** Author avatar next to an incoming message row. CORE-642 picks the file by this size. */
const MESSAGE_AVATAR_SIZE = 28;

/**
 * CORE-396 — backend-agnostic chat-thread UI. Renders a list of
 * {@link ThreadMessage} + composer + "thinking" indicator + quick replies,
 * driven purely by props. Knows nothing about GetStream vs Agape.ChatApi — the
 * caller owns the data source (a `ChatApiChannel`, a Stream channel adapter,
 * etc.) and passes `messages`/`onSend`. This is the seam that lets the chat UI
 * accept messages from either backend without the stream-chat-react coupling
 * that forced a bespoke panel in CORE-371.
 */
export interface AgapellaChatThreadProps {
  /** Presentation only; the homepage guest thread shares the same conversation behavior. */
  appearance?: 'default' | 'home';
  messages: ThreadMessage[];
  /** A companion is composing a reply → show the typing indicator. */
  thinking?: boolean;
  loading?: boolean;
  error?: boolean;
  /** Peer identity for incoming-message avatars + the "thinking" label. */
  peer: { name: string; avatarUrl?: string | null; avatarThumbUrl?: string | null; brandedAvatar?: ImageSourcePropType };
  /**
   * CORE-1350 (П3) — исход отправки возвращается сюда.
   *
   * Раньше тип был `void`, и композер очищал поле безусловно: отказу некуда было
   * прийти, а написанное исчезало вместе с ним. Источники, которым сказать
   * нечего (круги, события, DM), по-прежнему возвращают `void` — тогда исход
   * считается успешным, как и было.
   */
  onSend: (
    text: string,
    quotedParentId?: string,
    attachments?: ComposerAttachment[],
    /**
     * CORE-1749 — фраза из цитируемого сообщения, если человек отвечает на неё,
     * а не на всё сообщение. Приходит только вместе с цитатой (`quotedParentId`
     * здесь — id цитируемого, как и раньше); источники без фраз её не читают.
     */
    replyFragment?: string,
    /**
     * CORE-1791 (возврат QA 28.10) — ключ повтора. Один на попытку отправить
     * этот текст: повтор вернувшегося в поле текста идёт с тем же ключом, и
     * сервер отдаёт уже созданное сообщение вместо второй копии.
     */
    clientMessageId?: string,
  ) => void | Promise<SendResult | void>;
  placeholder?: string;
  /**
   * Composer attachment upload (slice 5d-4). When provided, the composer shows a
   * 📎 button + (web) file picker; picked files are validated/moderated/uploaded
   * via this fn and staged as chips, then sent alongside the text. Omitted ⇒
   * text-only composer (AI companion). Web-only.
   */
  onUpload?: ComposerUpload;
  /**
   * Voice recording in the web composer (CORE-1356). Its own flag on purpose:
   * the microphone used to ride in on `onUpload`, so the single line that gave
   * a thread attachments silently gave it voice too — no design, no word to
   * the person that speech goes off to be transcribed.
   *
   * Defaults to `true` so every thread that already has the microphone keeps
   * it; a new surface that only wants the 📎 passes `false` and says so out
   * loud. Still web-only and still requires `onUpload` — that is where the
   * recording is sent.
   */
  allowVoice?: boolean;
  /** `accept` attr for the file picker (from the channel's attachment limits). */
  acceptFileTypes?: string;
  /**
   * CORE-1682 — the limits in words («До 10 файлов, до 25 МБ каждый. PDF, …»):
   * the 📎 tooltip and a line under the staged files, so people read the rules
   * before a refusal does it for them.
   */
  attachmentHint?: string;
  /** CORE-1682 — how many files one message may carry; extra picks are refused. */
  maxAttachments?: number;
  /**
   * Names offered by the `@`-mention autocomplete (e.g. the council companions).
   * The council router routes a reply to the companion named after `@`, so the
   * menu just helps the user type the name. Empty ⇒ no mention menu.
   */
  mentionables?: { id: string; name: string }[];
  /**
   * CORE-1653 — люди, которых можно упомянуть (чат сообщества). В отличие от
   * `mentionables` упоминание привязано к человеку, а не к имени: в текст
   * уходит метка `@[Имя](user:<id>)`, тот же формат, что в Live (CORE-1421).
   * Пусто ⇒ подсказок нет; метки в чужих сообщениях всё равно рисуются.
   */
  mentionCandidates?: MentionCandidate[];
  /**
   * CORE-1684 — поиск по всей платформе для «@»: по нику и имени, включая тех,
   * кого в этом чате нет. Участники канала (`mentionCandidates`) отвечают
   * сразу, найденные встают следом. Нет — подсказки только из участников.
   */
  searchMentions?: (query: string) => Promise<MentionCandidate[]>;
  /** CORE-1653 — кто читает: своё упоминание выделяется плашкой. */
  myUserId?: string | null;
  /**
   * CORE-1653 — нажатие на автора (аватар, имя) или на упоминание. Зовётся
   * только для id, за которым есть профиль. Нет ⇒ ничего не нажимается.
   */
  onPressUser?: (userId: string) => void;
  /**
   * CORE-1653 — модератор удаляет и чужие сообщения. Сервер это право проверяет
   * сам; флаг лишь показывает пункт меню тому, кому он не откажет.
   */
  canDeleteAny?: boolean;
  /** Toggle a reaction. Omitted ⇒ reactions render read-only (no picker). */
  onReact?: (messageId: string, type: string, add: boolean) => void;
  /** Fired on keystroke to broadcast "I'm typing" (slice 5a). Caller throttles. */
  onTyping?: () => void;
  /** Edit one of my own messages (slice 5b). Omitted ⇒ no edit affordance. */
  onEdit?: (messageId: string, text: string) => void;
  /** Delete one of my own messages (slice 5b). Omitted ⇒ no delete affordance. */
  onDelete?: (messageId: string) => void;
  /**
   * Group chat (circle, CORE-404): render each incoming message with its own
   * sender avatar + a name label, instead of a single 1:1 `peer`.
   */
  group?: boolean;
  /** Optional content rendered above the message list (e.g. circle pinned bar). */
  headerSlot?: React.ReactNode;
  /**
   * CORE-1942 — строка прямо над полем ввода (гостевой разговор на главной
   * ставит сюда «Ответы формирует ИИ · Уведомление об ИИ»).
   */
  composerTopSlot?: React.ReactNode;
  /**
   * Mobile-only back affordance. When set, a compact top bar (← + peer) renders
   * above the thread so the user can return to the conversation list — the DM /
   * AI-companion panels otherwise have no header at all.
   */
  onBack?: () => void;
  /** Status line under the peer name in the back-header (e.g. «в сети»). */
  peerSubtitle?: string;
  /** Colour the subtitle green («в сети») vs muted (last-seen). */
  peerOnline?: boolean;
  /**
   * Tapping the peer (avatar + name) opens their public profile. When set,
   * the header renders on desktop too (not just when `onBack` provides a
   * mobile back bar) and the avatar/name block becomes a button — this is
   * how a human DM reaches the interlocutor's profile on web. AI / circle
   * panels leave it undefined (no human behind the channel). CORE-480.
   */
  onPressPeer?: () => void;
  /**
   * Enable quoted replies (slice 5d-3): a «Ответить» action in the message menu
   * quotes the message into the composer; sending passes its id as the quote
   * parent. Off ⇒ no reply affordance. CORE-1749 — the AI companion turns it on.
   */
  enableReply?: boolean;
  /**
   * CORE-1749 — веб: выделил текст в сообщении собеседника (Агапеллы) — рядом
   * кнопка «Спросить об этом», и над полем встаёт цитата с одной этой фразой.
   * Нужен `enableReply`: фраза едет как часть ответа.
   */
  enableFragmentReply?: boolean;
  /**
   * CORE-1683 — fetch history around a message that a quote points at but the
   * list does not hold (older than the loaded page). Resolves `true` once the
   * message is in `messages`; the thread then scrolls to it. Omitted ⇒ a quote
   * of an unloaded message just does nothing on tap.
   */
  onLoadAround?: (messageId: string) => Promise<boolean>;
  /**
   * CORE-1681 — a message to land on once the thread has loaded (a mention or
   * reply notification). Scrolled to and highlighted like a tapped quote; one
   * jump per value. Pulled in through `onLoadAround` when it is older than the
   * loaded page; if it cannot be found at all the thread says so instead of
   * silently staying at the bottom.
   */
  focusMessageId?: string | null;
  /** Block a group member by user id (slice 5d-3). Shown on others' messages. */
  onBlock?: (userId: string) => void;
  /**
   * CORE-995 — rename the moderation action for hosts where it means something
   * else. In a paid program the master silences a participant rather than
   * blocking them (the program is paid for), and a menu item labelled
   * «Заблокировать» would promise an action nobody performs. Omitted ⇒ the
   * default block wording, unchanged for every existing chat.
   */
  blockAction?: { label: string; confirm: (name: string) => string };
  /**
   * CORE-697 — the discussion's single pinned message, shown as a banner above
   * the thread; tapping it jumps to the message. Null/undefined ⇒ no banner.
   */
  pinned?: { messageId: string; text?: string | null } | null;
  /**
   * CORE-697 — pin/unpin from the message menu. Only set for someone allowed to
   * moderate; absent ⇒ no «Закрепить» item at all.
   */
  onPinToggle?: (m: ThreadMessage, pin: boolean) => void;
  /**
   * CORE-698 — «Отметить как непрочитанное»: the discussion returns to the list
   * with a badge, counted from this message. Absent ⇒ no such item.
   */
  onMarkUnread?: (m: ThreadMessage) => void;
  /** Mute gate (slice 5d-3): replace the composer with a hint when the current
   *  user is muted in this circle. */
  muted?: boolean;
  /** Russian hint shown in place of the composer while muted. */
  muteHint?: string;
  /**
   * Backend-specific message override (CORE-404). Return a node to render a
   * message as something other than a text bubble (e.g. a circle event card),
   * or null/undefined to fall through to the normal bubble. Keeps the thread
   * agnostic — the caller owns any Stream/circle-specific rendering.
   */
  renderSpecial?: (m: ThreadMessage) => React.ReactNode | null;
}

// CORE-1791 (2b) — в разделе сообщений поверх лежат ключи материала v3 (messagesV3Thread).
type Styles = ReturnType<typeof makeStyles> & Record<string, any>;

/**
 * CORE-1791 — в разделе сообщений (`hit` задан) нажимается квадрат 44, а
 * видимая часть остаётся прежней внутри него. Вне раздела — ровно прежний
 * `<Pressable style={visual}>`, без лишнего узла.
 */
const Hit: React.FC<PressableProps & { hit: StyleProp<ViewStyle> | null; visual: StyleProp<ViewStyle>; children?: React.ReactNode }> = ({
  hit, visual, children, ...rest
}) => (hit
  ? <Pressable {...rest} style={hit}><View style={visual}>{children}</View></Pressable>
  : <Pressable {...rest} style={visual}>{children}</Pressable>);

/** CORE-1791 (2b) — область ввода v3 (своя подложка и отступы) или, вне раздела, ничего. */
const ComposeWrap: React.FC<{ style: StyleProp<ViewStyle> | null; children: React.ReactNode }> = ({ style, children }) => (
  style ? <View style={style} testID="chat-compose-area">{children}</View> : <>{children}</>
);

/**
 * CORE-1791 (2b) — кнопка поля ввода v3: видимая подложка 40 (на телефоне — 44
 * без подложки) внутри области нажатия 44; наведение — тёплый слой, нажатие —
 * сдвиг на 1px (без него при reduced-motion).
 */
const V3Button: React.FC<PressableProps & { visual: StyleProp<ViewStyle>; hover: StyleProp<ViewStyle>; still?: boolean; children: React.ReactNode }> = ({
  visual, hover, still, children, ...rest
}) => (
  <Pressable
    {...rest}
    style={{ minWidth: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center', borderRadius: 14, ...(isWeb ? ({ cursor: rest.disabled ? 'default' : 'pointer' } as object) : null) }}
  >
    {(state: any) => (
      <View style={[visual, state.hovered && !rest.disabled && hover, state.pressed && !still && ({ transform: [{ translateY: 1 }] } as ViewStyle)]}>
        {children}
      </View>
    )}
  </Pressable>
);

/**
 * Brand reaction set (AMB-64), type↔emoji.
 *
 * CORE-1220 — список переехал в `constants/brandReactions`: к нему теперь
 * подключены и публикации, а копия здесь означала бы два набора, которые
 * разойдутся при первой правке.
 */
const REACTIONS = BRAND_REACTIONS;

const isWeb = Platform.OS === 'web';

/** A file staged in the composer (slice 5d-4): uploading → done/error. */
interface PendingAttachment {
  key: string;
  name: string;
  status: 'uploading' | 'done' | 'error';
  /** Object URL for an image thumbnail (revoked on remove/send). */
  previewUrl?: string;
  isImage: boolean;
  attachment?: ComposerAttachment;
  /** CORE-1682 — kept so a failed upload can be retried in place. */
  blob?: Blob;
  durationSec?: number;
  /** CORE-311 — why this file was refused; the notice leaves with the file. */
  refusal?: string;
}

/** The message being answered, shown above the composer. CORE-1749 — `fragment`: a phrase of it. */
interface ReplyDraft {
  id: string;
  text: string;
  senderName: string;
  mine?: boolean;
  fragment?: string;
}

/** CORE-1791 (QA 31.10) — why a send was refused and what it carried besides the text. */
interface ReturnedSend {
  clientMessageId: string;
  message: string;
  items: PendingAttachment[];
  reply: ReplyDraft | null;
}

/** CORE-1791 (QA 31.10) — a refused send coming back to the composer that shows its dialog. */
interface Refusal extends ReturnedSend {
  text: string;
}

/**
 * CORE-1791 (возврат QA 31.10) — какой тред сейчас показывает диалог (ключ
 * черновика). Отказ отправки возвращается туда, а не в тред, из которого
 * отправляли: тот мог уже смениться другим.
 */
const shownThreads = new Map<string, (r: Refusal) => void>();

/**
 * Отказ, пришедший, когда диалог не открыт нигде (тред снимается вместе с
 * каналом, и ожидающая отправка отменяется сразу). Текст уходит в черновик, а
 * причина, файлы и цитата ждут здесь до возврата к своему диалогу в этой
 * вкладке: без причины вернувшиеся текст и файл ничего не объясняют. Ключ —
 * ключ черновика (`user_<id>|…`), а возвращаются они только вместе с ещё живой
 * попыткой этого аккаунта: после выхода попытки стёрты.
 */
const returnedSends = new Map<string, ReturnedSend>();
/** Попытки, о пропавших файлах которых уже сказали в этой вкладке. */
const filesNoticed = new Set<string>();

/** A non-image attachment rendered as an icon + label chip (native / fallback). */
const AttachChip: React.FC<{
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  mine?: boolean;
  s: Styles;
  tc: ThemeColors;
}> = ({ icon, label, mine, s, tc }) => (
  <View style={s.attachChip}>
    <Ionicons name={icon} size={16} color={mine ? '#fff' : (s.attachChipIcon?.color ?? tc.solar)} />
    <Text style={[s.attachChipText, mine && s.bubbleTextMine]} numberOfLines={1}>
      {label}
    </Text>
  </View>
);

/** Lazy video (CORE-353 pattern): a poster + play badge that mounts the real
 *  <video> only on click, so a heavy player never blocks the message list. */
const VideoAttachment: React.FC<{ a: any; mine?: boolean; s: Styles; tc: ThemeColors }> = ({ a, mine, s, tc }) => {
  const [play, setPlay] = useState(false);
  const poster = a?.thumb_url || a?.image_url;
  if (!isWeb) return <AttachChip icon="videocam" label={a?.title || 'Видео'} mine={mine} s={s} tc={tc} />;
  if (play && a?.asset_url) {
    return <video controls autoPlay src={a.asset_url} style={{ maxWidth: 260, borderRadius: 12, marginBottom: 4 }} />;
  }
  return (
    <Pressable onPress={() => setPlay(true)} style={s.videoPoster}>
      {poster ? (
        <Image source={{ uri: poster }} style={s.attachImage} resizeMode="cover" />
      ) : (
        <View style={[s.attachImage, s.videoFallback]} />
      )}
      <View style={s.playBadge}>
        <Ionicons name="play" size={22} color="#fff" />
      </View>
    </Pressable>
  );
};

/** CORE-1739 — a community-chat file without a stored address. The server hands
 *  the file only to the member the one-off link was issued to, WITH their login
 *  token (QA FAIL 19.10: a forwarded fresh link opened for anyone first), so the
 *  file is fetched with `Authorization` and shown from memory — the picture as an
 *  object URL (the viewer reuses it), a document as a download from the blob.
 *  No access (unsubscribed, message deleted) → «Файл недоступен». */
const LinkedAttachmentView: React.FC<{
  a: any;
  mine?: boolean;
  s: Styles;
  tc: ThemeColors;
  onOpenImage?: (url: string, name?: string) => void;
}> = ({ a, mine, s, tc, onOpenImage }) => {
  const [unavailable, setUnavailable] = useState(false);
  const isImage = a.type === 'image';
  const image = useCommunityAttachmentImage(isImage ? (a.link_url as string) : null);
  if (unavailable || (isImage && image.failed)) {
    return <AttachChip icon="alert-circle-outline" label="Файл недоступен" mine={mine} s={s} tc={tc} />;
  }
  if (isImage) {
    if (!image.uri) return <View style={[s.attachImage, s.videoFallback]} />;
    const img = <Image source={{ uri: image.uri }} style={s.attachImage} resizeMode="cover" />;
    if (!onOpenImage) return img;
    const uri = image.uri;
    return (
      <Pressable
        onPress={() => onOpenImage(uri, a.file_name || a.title)}
        accessibilityRole="button"
        accessibilityLabel="Открыть изображение"
        testID="chat-attachment-image"
      >
        {img}
      </Pressable>
    );
  }
  return (
    <Pressable
      onPress={() => {
        void downloadCommunityAttachment(a.link_url as string, a.file_name || a.title || 'Файл')
          .then((ok) => { if (!ok) setUnavailable(true); });
      }}
      accessibilityRole="button"
      accessibilityLabel={`Скачать ${a.file_name || a.title || 'файл'}`}
      testID="chat-attachment-file"
    >
      <AttachChip icon="document-attach" label={attachmentCaption(a)} mine={mine} s={s} tc={tc} />
    </Pressable>
  );
};

/** One attachment of any type. Images render inline; video lazy-loads; voice gets
 *  an <audio> player on web; anything else becomes an open-link/file chip. */
const AttachmentView: React.FC<{
  a: any;
  mine?: boolean;
  s: Styles;
  tc: ThemeColors;
  /** CORE-1682 — open the full-size picture (thumbnail tap). */
  onOpenImage?: (url: string, name?: string) => void;
}> = ({ a, mine, s, tc, onOpenImage }) => {
  const { width } = useWindowDimensions();
  // CORE-1791 — в разделе сообщений «телефон» решает раскладка экрана (700), а не порог виджета (768).
  const surface = useMessagesSurface();
  const phone = surface ? surface.phone : isMobileWebLayout(isWeb, width);
  const type = a?.type;
  if (a?.link_url) return <LinkedAttachmentView a={a} mine={mine} s={s} tc={tc} onOpenImage={onOpenImage} />;
  if (type === 'image' && (a.image_url || a.thumb_url)) {
    const full = a.image_url || a.thumb_url;
    const img = <Image source={{ uri: a.thumb_url || a.image_url }} style={s.attachImage} resizeMode="cover" />;
    if (!onOpenImage) return img;
    return (
      <Pressable
        onPress={() => onOpenImage(full, a.file_name || a.title)}
        accessibilityRole="button"
        accessibilityLabel="Открыть изображение"
        testID="chat-attachment-image"
      >
        {img}
      </Pressable>
    );
  }
  if (type === 'video' || type === 'og:video') {
    return <VideoAttachment a={a} mine={mine} s={s} tc={tc} />;
  }
  if (type === 'voiceRecording' || type === 'audio') {
    if (isWeb && a?.asset_url) {
      // CORE-311 — phone width: play / pause and the time, without the browser's slider,
      // mute and overflow menu.
      if (phone) {
        return <VoiceMessagePlayer url={a.asset_url} durationSec={a.duration} mine={mine} tc={tc} />;
      }
      return <audio controls preload="none" src={a.asset_url} style={{ maxWidth: 240, marginBottom: 4 }} />;
    }
    return <AttachChip icon="mic" label={a?.title || 'Голосовое сообщение'} mine={mine} s={s} tc={tc} />;
  }
  // Generic file / link preview → open in a new tab (web) or a labelled chip.
  // CORE-1682 — a stored document names itself: «отчёт.pdf · PDF · 1,2 МБ»,
  // and the link downloads it under its original name.
  const url = a?.asset_url || a?.og_scrape_url || a?.image_url;
  const stored = !!a?.file_name || !!a?.file_size;
  const label = stored ? attachmentCaption(a) : (a?.title || a?.fallback || 'Файл');
  if (!url && !a?.text) return null;
  if (isWeb && url) {
    return (
      <a
        href={a?.download_url || url}
        target="_blank"
        rel="noreferrer"
        style={{ textDecoration: 'none' }}
        {...(stored ? { download: a.file_name || a.title || true } : {})}
      >
        <AttachChip icon="document-attach" label={label} mine={mine} s={s} tc={tc} />
      </a>
    );
  }
  return <AttachChip icon="document-attach" label={label} mine={mine} s={s} tc={tc} />;
};

/** HH:MM for a message's send time (footer under each bubble). */
const timeOf = (d: Date): string =>
  d instanceof Date && !Number.isNaN(d.getTime())
    ? `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    : '';

const RU_MONTHS_GEN = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
// Null-safe: a just-sent (optimistic) message can arrive without a valid Date —
// treat that as «same day» so no divider is inserted and the list never crashes
// (an unguarded getFullYear() on undefined blanked the whole thread on send).
const sameDay = (a?: Date | null, b?: Date | null): boolean => {
  if (!(a instanceof Date) || !(b instanceof Date) || Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return true;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

/** «Сегодня» / «Вчера» / «9 апреля» / «9 апреля 2024» — date divider label (mirrors native). */
const dayLabel = (d: Date): string => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  const today = new Date();
  if (sameDay(d, today)) return 'Сегодня';
  const yest = new Date(today);
  yest.setDate(today.getDate() - 1);
  if (sameDay(d, yest)) return 'Вчера';
  const base = `${d.getDate()} ${RU_MONTHS_GEN[d.getMonth()]}`;
  return d.getFullYear() !== today.getFullYear() ? `${base} ${d.getFullYear()}` : base;
};

/**
 * One labelled row of the message menu. CORE-696 — the menu used to be a strip
 * of bare icons, so even once you found it you had to guess what each one did;
 * these read the way the native menu does.
 */
const MenuAction: React.FC<{
  s: Styles;
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  color: string;
  danger?: boolean;
  onPress: () => void;
  /** CORE-1791 — раздел сообщений: строка не ниже 44. */
  hs?: HitStyles | null;
}> = ({ s, icon, label, color, danger, onPress, hs }) => (
  <Pressable onPress={onPress} style={[s.menuItem, hs?.menuItem]} accessibilityLabel={label}>
    <Ionicons name={icon} size={17} color={color} />
    <Text style={[s.menuLabel, danger && s.menuLabelDanger]}>{label}</Text>
  </Pressable>
);

/**
 * CORE-1749 — метка тела сообщения для «Спросить об этом» на вебе:
 * `data-fragment-source="<id>"`. Выделение засчитывается, только если оба его
 * конца внутри одной такой метки (см. `fragmentSelection`). Выключенная — не
 * добавляет в дерево ничего.
 */
const FragmentSourceWrap: React.FC<{ enabled: boolean; messageId: string; children: React.ReactNode }> = ({
  enabled, messageId, children,
}) => (enabled ? <View {...({ dataSet: { fragmentSource: messageId } } as object)}>{children}</View> : <>{children}</>);

/** One message: quoted preview + image attachments + text + reactions + ✓✓. */
const MessageRow: React.FC<{
  m: ThreadMessage;
  peer: { name: string; avatarUrl?: string | null; avatarThumbUrl?: string | null; brandedAvatar?: ImageSourcePropType };
  s: Styles;
  tc: ThemeColors;
  onReact?: (messageId: string, type: string, add: boolean) => void;
  /** Begin editing this (own) message — lifts its text into the composer. */
  onEditStart?: (m: ThreadMessage) => void;
  /** Delete this (own) message. */
  onDelete?: (messageId: string) => void;
  /** Send a canned reply (AI highlight tap). */
  onReply?: (text: string) => void;
  /** Begin a quoted reply to this message (slice 5d-3). */
  onReplyStart?: (m: ThreadMessage) => void;
  /** CORE-1683 — the quote block was tapped: go to the original. */
  onPressQuote?: (messageId: string) => void;
  /**
   * CORE-1749 — the body is a source of phrases to ask about (web): marked so
   * {@link FragmentAskButton} knows a selection lies inside this message only.
   */
  fragmentSource?: boolean;
  /** CORE-1683 — this row is the target of a jump: paint it so the eye finds it. */
  highlighted?: boolean;
  /** CORE-1682 — open an image attachment full-size. */
  onOpenImage?: (url: string, name?: string) => void;
  /** Block this message's sender (slice 5d-3) — others' messages only. */
  onBlock?: (userId: string) => void;
  /** CORE-995 — wording override for the action above. */
  blockAction?: { label: string; confirm: (name: string) => string };
  /** Report this message (CORE-26 flow) — others' messages only. */
  onReport?: (m: ThreadMessage) => void;
  /** CORE-697 — this message is the discussion's pin. */
  isPinned?: boolean;
  /** CORE-697 — pin/unpin; absent for anyone who may not moderate. */
  onPinToggle?: (m: ThreadMessage, pin: boolean) => void;
  /** CORE-698 — mark the discussion unread from this message onwards. */
  onMarkUnread?: (m: ThreadMessage) => void;
  /** CORE-697 — report this row's offset + height (the open menu is part of it). */
  onMeasure?: (messageId: string, y: number, height: number) => void;
  /**
   * CORE-697 — the menu for this row just opened. The menu renders inline under
   * the bubble, inside the scrolling list, so on the last message of a thread
   * that sits at its bottom it lands below the fold and reads as «the menu no
   * longer opens». The list owner scrolls it into view.
   */
  onMenuOpen?: (messageId: string) => void;
  /** Group chat (circle): use each message's own sender identity + name label. */
  group?: boolean;
  /** CORE-707 — shared badge for this group-message author. */
  senderBadge?: LevelBadgeData | null;
  senderBadgeStatus: LevelBadgeLoadStatus;
  /** Backend-specific override — renders in place of the text bubble if non-null. */
  renderSpecial?: (m: ThreadMessage) => React.ReactNode | null;
  /** CORE-1653 — см. одноимённые свойства ленты. */
  myUserId?: string | null;
  onPressUser?: (userId: string) => void;
  canDeleteAny?: boolean;
  /** CORE-311 — phone-width web: the menu fits the screen and closes on a tap outside. */
  mobileWeb?: boolean;
  /** CORE-1791 — области нажатия раздела сообщений; `null` — вне раздела, всё как было. */
  hs: HitStyles | null;
  /** CORE-1791 (2b) — материал v3 раздела сообщений; `null` — вне раздела. */
  v3?: MessagesV3 | null;
}> = ({ m, peer, s, tc, onReact, onEditStart, onDelete, onReply, onReplyStart, onPressQuote, fragmentSource, highlighted, onOpenImage,
        onBlock, blockAction, onReport, isPinned, onPinToggle, onMarkUnread, onMeasure, onMenuOpen,
        group, senderBadge, senderBadgeStatus, renderSpecial, myUserId, onPressUser, canDeleteAny, mobileWeb, hs, v3 = null }) => {
  const [picker, setPicker] = useState(false);
  const { t: tChats } = useI18n('chats');
  // CORE-1479 — наградные реакции: под замком или вне выбора, пока не открыты.
  const { access: rewardAccess, guardAdd } = useRewardReactions();
  // CORE-311 — on mobile web the menu floats over the thread, opening from the «…».
  const dotsRef = useRef<View>(null);
  const [menuAnchor, setMenuAnchor] = useState<MenuAnchor | null>(null);
  // Measured first: the floating card opens from where the «…» is on screen.
  // No scroll-to-menu here — the list stays put under the overlay.
  const openFloatingMenu = useCallback(() => {
    dotsRef.current?.measureInWindow((x, y, width, height) => {
      setMenuAnchor({ x, y, width, height });
      setPicker(true);
    });
  }, []);
  const toggleMenu = useCallback(() => {
    const next = !picker;
    if (next && mobileWeb) return openFloatingMenu();
    setPicker(next);
    if (next) onMenuOpen?.(m.id);
  }, [picker, m.id, onMenuOpen, mobileWeb, openFloatingMenu]);
  // CORE-696 — on web the menu used to be reachable only by holding the mouse
  // button down for half a second, which nobody does with a mouse: the actions
  // read as missing. The row now reveals a «…» button on hover.
  const [hovered, setHovered] = useState(false);
  // CORE-1685 — своя реакция сравнивается по слагу комплекта: сервер может
  // отдать прежний слаг (love), а чип — новый (agape_1), и это одна реакция.
  // CORE-1479 (возврат QA 23.10) — по всем своим, а не по первой: сервер хранит
  // реакции по типу, и своих может оказаться две. Какой слаг снимать, решает
  // источник (`myReactionCalls`), — здесь только «моя ли».
  const isMine = (type: string) => ownReactionSlugs(m.myReactions, type).length > 0;
  // CORE-1479 — закрытую наградную не ставим: человек видит подсказку.
  const isLocked = (type: string) => !isMine(type) && rewardAccess(type) === 'locked';
  const pickReaction = (type: string) => {
    const add = !isMine(type);
    if (add && !guardAdd(type)) return;
    onReact?.(m.id, type, add);
    setPicker(false);
  };
  const attachments: any[] = m.attachments || [];
  // CORE-353 — Agape.ChatApi doesn't enrich pasted links (GetStream did), so a
  // practice link arrives as plain text with no attachment. Re-derive a preview
  // from the text, unless the message already carries a video attachment.
  const hasVideoAttachment = attachments.some((a) => a?.type === 'video' || a?.type === 'og:video');
  const practiceIds = useMemo(
    () => (hasVideoAttachment ? [] : extractPracticeIds(m.text)),
    [m.text, hasVideoAttachment],
  );
  // CORE-1730 — одни фирменные эмодзи (1–3) без вложений и подсветки:
  // прозрачный контейнер и крупные картинки вместо пузыря с текстом. Правило
  // ТЗ одно на весь веб, раздел сообщений включительно (решение владельца
  // 03.11): четыре и больше или любой текст рядом — обычный пузырь.
  const emojiOnly = useMemo(
    () => (attachments.length === 0 && !m.highlights?.length ? emojiOnlyTokens(m.text, EMOJI_ONLY_MAX) : null),
    [m.text, m.highlights, attachments.length],
  );
  // `onBrandFill` — текст лежит на оранжевой заливке своего пузыря: только тогда
  // нужны белые ссылки, упоминания, время и подписи вложений.
  // CORE-1791 (2b) — в разделе сообщений оранжевой заливки у своих пузырей нет
  // вовсе: белые ссылки, подписи и значки вложений там не нужны.
  const openSurface = !!emojiOnly || !!v3;
  const onBrandFill = m.isMine && !openSurface;

  // Backend-specific full-width render (e.g. a circle event card) replaces the
  // whole bubble/avatar row when the caller claims this message.
  const special = renderSpecial?.(m);
  if (special) return <View style={s.msgWrap}>{special}</View>;
  // In a group, each incoming message shows its OWN sender (not a single peer).
  const inName = group ? (m.senderName || peer.name) : peer.name;
  // CORE-642 — the incoming-message avatar renders at MESSAGE_AVATAR_SIZE, so
  // prefer each side's preview when the backend produced one.
  const inAvatar = group
    ? pickAvatarUrl(m.senderAvatar, m.senderAvatarThumb, MESSAGE_AVATAR_SIZE)
    : pickAvatarUrl(peer.avatarUrl, peer.avatarThumbUrl, MESSAGE_AVATAR_SIZE);
  // CORE — Agapella's message-row avatar must match the header/sidebar, which
  // hard-code the branded local asset for the default assistant. In a 1:1 with
  // her, prefer that asset over the backend-configured avatarUrl so the two
  // surfaces never diverge. Groups (council) keep per-sender avatars.
  const inBrandedAvatar = !group ? peer.brandedAvatar : undefined;
  // Own-message edit/delete live in the same long-press menu as reactions.
  const canManage = m.isMine && (!!onEditStart || !!onDelete);
  // CORE-1653 — удалить чужое может модератор; своё — как и раньше, автор.
  const canDelete = !!onDelete && (m.isMine || !!canDeleteAny);
  // CORE-1653 — автор с профилем: аватар и имя ведут на его страницу.
  const openSender = group && !m.isMine && onPressUser && isProfileUserId(m.senderId)
    ? () => onPressUser(m.senderId!)
    : undefined;
  // Reply (quote) is offered on any message; block only on others' messages.
  const canReply = !!onReplyStart;
  const canBlock = !m.isMine && !!onBlock && !!m.senderId;
  // Complaining about your own message makes no sense — same rule as native.
  const canReport = !m.isMine && !!onReport;
  const canPin = !!onPinToggle;
  // CORE-735 — available on your own messages too. It used to be hidden there
  // because unread never counted the reader's own messages, so the badge would
  // have come back 0; but in a master's own community chat the last message is
  // usually theirs, and a silently missing action reads as a broken feature
  // (QA fail on CORE-698). The backend now counts own messages for a pointer
  // that was rewound by hand, and only for that.
  const canMarkUnread = !!onMarkUnread;
  const canOpenMenu = !!onReact || canManage || canReply || canBlock || canReport || canPin || canMarkUnread || canDelete;

  // The «…» affordance sits on the outer side of the bubble. It is always there
  // — faint at rest, solid under the pointer or while the menu is open. Hiding
  // it until hover would leave touch-screen web users (who never hover) exactly
  // where they started, and it is the discoverability that was missing.
  const menuButton = isWeb && canOpenMenu && (v3 ? (
    // CORE-1791 — в разделе сообщений «…» стоит в гаттере с внешней стороны
    // пузыря: якорь нулевой ширины, кнопка от него абсолютно, пузыри не
    // сдвигаются. С мышью она прозрачна до наведения на строку, фокуса внутри
    // строки или открытого меню (global.css, data-msg-menu) — но остаётся в
    // порядке Tab и в дереве доступности. На тач-устройствах видна всегда.
    <View style={s.menuSlot}>
      <Pressable
        ref={dotsRef}
        onPress={toggleMenu}
        accessibilityRole="button"
        accessibilityLabel="Действия с сообщением"
        testID="chat-message-menu"
        {...({
          'aria-haspopup': 'menu',
          'aria-expanded': picker,
          dataSet: { msgMenu: 'v3' },
          title: 'Действия с сообщением',
        } as object)}
        style={[s.menuHit, m.isMine ? s.menuHitMine : s.menuHitTheirs]}
      >
        {({ hovered: faceHovered }: any) => (
          <View style={[s.menuFace, faceHovered && s.menuFaceHover]} {...({ dataSet: { msgMenuFace: 'v3' } } as object)}>
            <MessagesIcon name="more" size={16} color={v3.t.muted} />
          </View>
        )}
      </Pressable>
    </View>
  ) : (
    <Pressable
      ref={dotsRef}
      onPress={toggleMenu}
      style={[s.menuBtn, hs?.menuBtn, (hovered || picker) && s.menuBtnActive]}
      accessibilityLabel="Действия с сообщением"
      {...({ title: 'Действия с сообщением' } as object)}
    >
      <Ionicons name="ellipsis-horizontal" size={16} color={tc.textMuted} />
    </Pressable>
  ));

  return (
    <View
      nativeID={`chat-msg-${m.id}`}
      style={[s.msgWrap, highlighted && s.msgWrapHighlighted]}
      onLayout={onMeasure ? (e) => onMeasure(m.id, e.nativeEvent.layout.y, e.nativeEvent.layout.height) : undefined}
    >
      {isPinned && (
        <View style={[s.pinnedChip, m.isMine && s.alignSelfEnd]}>
          <Ionicons name="pin" size={11} color={tc.solar} style={{ transform: [{ rotate: '45deg' }] }} />
          <Text style={s.pinnedChipText}>Закреплено</Text>
        </View>
      )}
      <Pressable
        onHoverIn={isWeb ? () => setHovered(true) : undefined}
        onHoverOut={isWeb ? () => setHovered(false) : undefined}
        // CORE-1791 (возврат QA 23.10) — строка нужна для наведения, а не для
        // фокуса: на вебе Pressable становился остановкой Tab без роли и
        // действия, и на каждое сообщение выходило три остановки вместо одной «…».
        {...(isWeb ? ({ tabIndex: -1 } as object) : {})}
        style={[s.row, m.isMine ? s.rowMine : s.rowTheirs]}
        // CORE-1791 — строка целиком: по её наведению и фокусу внутри показывается «…».
        {...(v3 ? ({ dataSet: { msgRow: 'v3' } } as object) : {})}
      >
        {m.isMine && menuButton}
        {!m.isMine && !(v3 && !group) && (() => {
          // CORE-1791 (2b) — в личном диалоге макет v3 без аватара у каждого
          // сообщения: собеседник один, его аватар в шапке. Группы — как были.
          const avatar = inBrandedAvatar ? (
            <Image source={inBrandedAvatar} style={s.avatar} />
          ) : inAvatar ? (
            <Image source={{ uri: inAvatar }} style={s.avatar} />
          ) : (
            <View style={[s.avatar, s.avatarFallback]}>
              <Text style={s.avatarLetter}>{(inName || '?').charAt(0)}</Text>
            </View>
          );
          if (!openSender) return avatar;
          return (
            <Pressable
              onPress={openSender}
              testID="chat-message-author-avatar"
              accessibilityRole="link"
              accessibilityLabel={`Открыть профиль: ${inName || 'участник'}`}
            >
              {avatar}
            </Pressable>
          );
        })()}
        <Pressable
          onLongPress={canOpenMenu ? toggleMenu : undefined}
          // Долгое нажатие — для пальца; с клавиатуры то же меню открывает «…».
          {...(isWeb ? ({ tabIndex: -1 } as object) : {})}
          style={[
            s.bubble,
            group && s.groupBubble,
            emojiOnly ? s.bubbleEmojiOnly : (m.isMine ? s.bubbleMine : s.bubbleTheirs),
          ]}
          testID={m.isMine ? 'chat-message-mine' : 'chat-message-theirs'}
          // Selecting text in your own message made it vanish: the global
          // ::selection paints selected text brand-orange, which is also this
          // bubble's background, so white-on-orange became orange-on-orange.
          // The marker lets one CSS rule in App.tsx re-colour selection on this
          // one surface; see it for the rule itself. Web-only: `dataSet` is a
          // react-native-web affordance and means nothing on native.
          {...(isWeb && m.isMine && !openSurface ? { dataSet: { ownBubble: 'true' } } as any : {})}
        >
          {group && !m.isMine && (!!inName || senderBadgeStatus !== 'disabled') && (
            <View style={s.groupSenderRow}>
              {!!inName && (
                <Text
                  style={s.groupSender}
                  onPress={openSender}
                  testID={openSender ? 'chat-message-author-name' : undefined}
                  accessibilityRole={openSender ? 'link' : undefined}
                >
                  {inName}
                </Text>
              )}
              <LevelBadge badge={senderBadge} status={senderBadgeStatus} size="compact" style={s.groupSenderBadge} />
            </View>
          )}
          {(() => {
            // CORE-1683 — the answered message (`replyTo`) is the quote; the
            // thread parent (`quoted`) only stands in where a backend has no
            // separate field. A deleted original keeps the reply, marked.
            const quote = m.replyTo ?? m.quoted;
            if (!quote) return null;
            const deleted = !!m.replyTo?.deleted;
            const canJump = !deleted && !!onPressQuote;
            return (
              <Pressable
                style={[s.quoted, openSurface && s.quotedEmojiOnly]}
                onPress={canJump ? () => onPressQuote!(quote.id) : undefined}
                disabled={!canJump}
                accessibilityRole={canJump ? 'button' : undefined}
                accessibilityLabel={canJump ? 'Перейти к исходному сообщению' : undefined}
                testID="chat-message-quote"
              >
                {deleted ? (
                  <Text style={[s.quotedText, onBrandFill ? s.quotedDeletedMine : s.quotedDeleted]}>
                    Исходное сообщение удалено
                  </Text>
                ) : (
                  <>
                    {/* CORE-1791 — в разделе своё — «Вы» (тот же источник, что «Вы:» в списке), чужое — имя с нейтральным запасным. */}
                    <Text style={s.quotedSender}>
                      {v3
                        ? (quote.isMine ? tChats('sidebar.you') : (quote.senderName || tChats('chat.unnamedUser')))
                        : quote.senderName}
                    </Text>
                    <Text style={s.quotedText} numberOfLines={2}>
                      {/* CORE-1749 — ответ на фразу показывает фразу, а не начало сообщения. */}
                      <EmojiText
                        text={m.replyTo
                          ? quoteLine(m.replyTo, (t) => replyPreviewSource(t, m.replyTo?.attachments))
                          : replyPreviewSource(quote.text)}
                        size={16}
                      />
                    </Text>
                  </>
                )}
              </Pressable>
            );
          })()}
          {attachments.map((a: any, i: number) => (
            <AttachmentView key={`att-${i}`} a={a} mine={onBrandFill} s={s} tc={tc} onOpenImage={onOpenImage} />
          ))}
          <FragmentSourceWrap enabled={!!fragmentSource && isWeb && !!m.text && !emojiOnly} messageId={m.id}>
          {m.highlights && m.highlights.length > 0 ? (
            // AI inline highlights replace the plain body; tap sends the canned
            // reply through the thread source (bare = host already draws bubble).
            <HighlightedText bare text={m.text} highlights={m.highlights} onReply={onReply} messageId={m.id} />
          ) : emojiOnly ? (
            // CORE-1730 — 1–3 эмодзи комплекта без текста: крупно и без пузыря.
            <EmojiOnlyMessage types={emojiOnly} mine={m.isMine} />
          ) : (
            !!m.text && (
              <Text style={[s.bubbleText, onBrandFill && s.bubbleTextMine]}>
                {/* CORE-1497 — шрифт пузыря передаётся и в размеченные куски:
                    без этого жирное, курсив и ссылки уезжали в системный шрифт,
                    и внутри одного ответа получались два разных. */}
                {/* CORE-1653 — упоминание вырезается ДО разметки: метка
                    `@[Имя](user:id)` для разборщика ссылок выглядит как
                    обычная ссылка, и он увёл бы нажатие по адресу `user:…`. */}
                {parseMentions(m.text).map((seg, i) => (seg.type === 'text' ? (
                  // CORE-1685 — эмодзи комплекта (`:agape_N:`) рисуются
                  // картинками в строке; остальное — как раньше, со ссылками.
                  <EmojiText
                    key={`seg-${i}`}
                    text={seg.value}
                    size={v3 ? V3_EMOJI_INLINE : undefined}
                    glyphStyle={v3 ? V3_INLINE_EMOJI_STYLE : undefined}
                    render={(chunk) => renderTextWithLinks(
                      chunk,
                      onBrandFill ? [s.link, s.linkMine] : s.link,
                      undefined,
                      uiFont(),
                    )}
                  />
                ) : (
                  <Text
                    key={`seg-${i}`}
                    testID="chat-mention"
                    onPress={onPressUser && isProfileUserId(seg.userId) ? () => onPressUser(seg.userId) : undefined}
                    style={[
                      s.mention,
                      onBrandFill && s.mentionMine,
                      !!myUserId && seg.userId === myUserId && s.mentionSelf,
                    ]}
                  >
                    @{seg.name}
                  </Text>
                )))}
              </Text>
            )
          )}
          </FragmentSourceWrap>
          {practiceIds.map((pid) => (
            <ChatPracticePreview key={`prev-${pid}`} id={pid} mine={m.isMine} />
          ))}
          {/* Footer: time under every message (like native), + ✓✓ on own. */}
          {/* CORE-1730 — без пузыря время приглушённое с обеих сторон, галочки
              акцентные: белое на прозрачном в светлой теме не видно. */}
          <View style={[s.metaRow, !!emojiOnly && !m.isMine && s.metaRowStart, v3 && !!emojiOnly && s.emojiStamp]}>
            <Text style={[s.msgTime, onBrandFill ? s.msgTimeMine : s.msgTimeTheirs]}>{timeOf(m.createdAt)}</Text>
            {m.isMine && (v3 ? (
              // CORE-1791 (2b) — значок доставки макета 16×12, статус из данных:
              // прочитано — две галочки, отправлено — одна.
              <View accessible accessibilityLabel={tChats(m.read ? 'chat.receiptRead' : 'chat.receiptSent')}>
                <MessagesIcon name={m.read ? 'read' : 'sent'} size={16} color={v3.t.accent} testID="chat-message-receipt" />
              </View>
            ) : (
              <Text style={[s.receipt, !onBrandFill && s.receiptEmojiOnly]}>{m.read ? '✓✓' : '✓'}</Text>
            ))}
          </View>
        </Pressable>
        {!m.isMine && menuButton}
      </Pressable>

      {m.cards && m.cards.length > 0 && (
        <View style={s.cardsWrap}>
          <RecommendationCards cards={m.cards} messageId={m.id} />
        </View>
      )}

      {/* CORE-1751 — Агапелла предлагает записать рефлексию по Live-сессии. */}
      {!m.isMine && m.reflectionOffer && (
        <View style={s.cardsWrap}>
          <ReflectionOfferButton offer={m.reflectionOffer} />
        </View>
      )}

      {/* CORE-1781 — бесплатные сообщения кончились: кнопка окна подписки. */}
      {!m.isMine && m.subscriptionRequired && (
        <View style={s.cardsWrap}>
          <AgapellaSubscriptionOfferButton messageId={m.id} />
        </View>
      )}

      {picker && canOpenMenu && (
        <MessageMenuLayer overlay={!!mobileWeb} anchor={menuAnchor} alignEnd={m.isMine} onClose={() => setPicker(false)}>
        <View style={[s.menuCard, m.isMine && s.alignSelfEnd, mobileWeb && [messageMenuMobileStyles.card, { backgroundColor: tc.surfaceDialog }]]}>
          {onReact && (
            <View style={[s.picker, mobileWeb && messageMenuMobileStyles.picker, hs?.picker]}>
              {REACTIONS.filter((r) => isMine(r.type) || rewardAccess(r.type) !== 'hidden').map((r) => (
                <Pressable
                  key={r.type}
                  onPress={() => pickReaction(r.type)}
                  style={[s.pickerItem, hs?.pickerItem, isMine(r.type) && s.pickerItemMine]}
                  accessibilityRole="button"
                  accessibilityLabel={r.label}
                  testID={`chat-reaction-pick-${r.type}${isLocked(r.type) ? '-locked' : ''}`}
                  {...(typeof document !== 'undefined' ? ({ title: r.label } as object) : {})}
                >
                  {/* CORE-1479 (возврат QA 28.10) — тускнеет картинка, а не кнопка
                      с замком: прозрачность кнопки гасила и замок (2,11:1). */}
                  <View style={isLocked(r.type) ? { opacity: 0.45 } : undefined}>
                    <ReactionGlyph type={r.type} size={26} />
                  </View>
                  {isLocked(r.type) && <RewardLock />}
                </Pressable>
              ))}
            </View>
          )}
          {canReply && (
            <MenuAction
              s={s}
              hs={hs}
              icon="arrow-undo-outline"
              label="Ответить"
              color={tc.textPrimary}
              onPress={() => { setPicker(false); onReplyStart!(m); }}
            />
          )}
          {canPin && (
            <MenuAction
              s={s}
              hs={hs}
              icon={isPinned ? 'pin' : 'pin-outline'}
              label={isPinned ? 'Открепить' : 'Закрепить'}
              color={tc.textPrimary}
              onPress={() => { setPicker(false); onPinToggle!(m, !isPinned); }}
            />
          )}
          {canMarkUnread && (
            <MenuAction
              s={s}
              hs={hs}
              icon="mail-unread-outline"
              label="Отметить как непрочитанное"
              color={tc.textPrimary}
              onPress={() => { setPicker(false); onMarkUnread!(m); }}
            />
          )}
          {canReport && (
            <MenuAction
              s={s}
              hs={hs}
              icon="flag-outline"
              label="Пожаловаться"
              color={tc.textPrimary}
              onPress={() => { setPicker(false); onReport!(m); }}
            />
          )}
          {canBlock && (
            <MenuAction
              s={s}
              hs={hs}
              icon={blockAction ? 'volume-mute-outline' : 'ban-outline'}
              label={blockAction?.label ?? 'Заблокировать'}
              color={v3 ? v3.t.danger : tc.error}
              danger
              onPress={() => {
                setPicker(false);
                const who = inName || 'участника';
                const question = blockAction
                  ? blockAction.confirm(who)
                  : `Заблокировать ${who}?`;
                if (typeof window === 'undefined' || window.confirm(question)) {
                  onBlock!(m.senderId!);
                }
              }}
            />
          )}
          {canManage && onEditStart && !!m.text && (
            <MenuAction
              s={s}
              hs={hs}
              icon="pencil"
              label="Редактировать"
              color={tc.textPrimary}
              onPress={() => { setPicker(false); onEditStart(m); }}
            />
          )}
          {canDelete && (
            <MenuAction
              s={s}
              hs={hs}
              icon="trash-outline"
              label="Удалить"
              color={v3 ? v3.t.danger : tc.error}
              danger
              onPress={() => {
                setPicker(false);
                const ask = m.isMine ? 'Удалить сообщение?' : `Удалить сообщение участника ${inName || ''}?`.replace(' ?', '?');
                if (!onDelete) return;
                if (typeof window === 'undefined' || window.confirm(ask)) onDelete(m.id);
              }}
            />
          )}
        </View>
        </MessageMenuLayer>
      )}

      {m.reactions.length > 0 && (
        <View style={[s.reactionsRow, m.isMine ? s.alignSelfEnd : s.reactionsTheirs]}>
          {/* CORE-1479 (возврат QA 23.10) — `love` и `agape_1` одной ладонью — один
              чип с общим счётом: два одинаковых рядом оба подсвечивались своими,
              хотя своя из них одна. */}
          {groupByCanonicalReaction(m.reactions).map(({ type, count }) => {
            const mine = isMine(type);
            return (
              <Hit
                key={type}
                hit={hs?.reactionChip ?? null}
                visual={[s.reactionChip, mine && s.reactionChipMine]}
                disabled={!onReact}
                onPress={() => {
                  if (!onReact) return;
                  if (!mine && !guardAdd(type)) return;
                  onReact(m.id, type, !mine);
                }}
                accessibilityRole="button"
                accessibilityState={{ selected: mine }}
                testID={mine ? 'chat-reaction-mine' : 'chat-reaction'}
              >
                <ReactionGlyph type={type} size={18} />
                {/* CORE-1685 (QA 16.10) — счётчик у каждой непустой реакции, включая 1. */}
                <Text style={s.reactionCount} testID="chat-reaction-count">{count}</Text>
              </Hit>
            );
          })}
        </View>
      )}
    </View>
  );
};

/** m:ss from milliseconds, for the voice-recording timer. */
function fmtDuration(ms: number): string {
  const total = Math.floor(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** The `@token` currently being typed at the end of the input, or null. */
function activeMention(text: string): string | null {
  const m = /(?:^|\s)@([^\s@]*)$/.exec(text);
  return m ? m[1] : null;
}

const NO_MENTION_CANDIDATES: MentionCandidate[] = [];

export const AgapellaChatThread: React.FC<AgapellaChatThreadProps> = ({
  appearance = 'default',
  messages,
  thinking = false,
  loading = false,
  error = false,
  peer,
  onSend,
  placeholder,
  mentionables = [],
  mentionCandidates = NO_MENTION_CANDIDATES,
  searchMentions,
  myUserId,
  onPressUser,
  canDeleteAny = false,
  onReact,
  onTyping,
  onEdit,
  onDelete,
  group = false,
  headerSlot,
  composerTopSlot,
  onBack,
  peerSubtitle,
  peerOnline = false,
  onPressPeer,
  renderSpecial,
  enableReply = false,
  enableFragmentReply = false,
  onLoadAround,
  focusMessageId,
  onBlock,
  blockAction,
  pinned,
  onPinToggle,
  onMarkUnread,
  muted = false,
  muteHint,
  onUpload,
  allowVoice = true,
  acceptFileTypes,
  attachmentHint,
  maxAttachments,
}) => {
  const { colors: tc, isDark } = useTheme();
  // CORE-1791 — раздел сообщений (/chats): области нажатия 44, черновик по
  // диалогу, лента прижата к низу. Вне раздела (`surface === null`) — как было.
  const surface = useMessagesSurface();
  // CORE-1791 (2b) — материал v3 переписки: только в разделе и только на вебе.
  const v3Base = useMessagesV3(surface?.layout ?? 'wide');
  const { t: tChats } = useI18n('chats');
  const v3: MessagesV3 | null = surface && isWeb ? v3Base : null;
  const { width: windowWidth } = useWindowDimensions();
  const compactHomeChat = windowWidth < 768;
  const styles = useMemo((): Styles => {
    const base = makeStyles(tc, isDark);
    if (appearance === 'home') return overlayStyles(base, homeChatAppearance(isDark, compactHomeChat));
    return v3 ? overlayStyles(base, makeV3ThreadStyles(v3)) : base;
  }, [tc, isDark, v3, appearance, compactHomeChat]);
  const hs = useMemo(() => (surface ? makeHitStyles() : null), [surface]);
  // CORE-311 — phone-width web gets the rounded composer field (see ComposerField).
  // CORE-1791 — в разделе сообщений «телефон» — раскладка экрана (уже 900 или
  // телефон боком, `chatsLayout`), а не порог виджета 768: иначе на части
  // ширин список и диалог стояли бы рядом, а поле ввода было бы телефонным.
  const mobileComposer = surface ? surface.phone : isMobileWebLayout(isWeb, windowWidth);
  // CORE-1730 — черновик с картинками эмодзи (DraftField) на ширине <768.
  // CORE-1791 (возврат QA 23.10) — в разделе сообщений на любой ширине: в
  // макете v3 эмодзи из палитры встаёт в черновик картинкой 28, а на desktop
  // поле показывало `:agape_3:` текстом. Вне раздела — как решено в CORE-1730.
  const draftFieldComposer = composerUsesDraftField({ section: !!v3, mobileComposer });
  const mStyles = useMemo(() => {
    const base = makeMobileComposerStyles(tc, isDark);
    return appearance === 'home' ? overlayStyles(base, homeChatAppearance(isDark, compactHomeChat)) : base;
  }, [tc, isDark, appearance, compactHomeChat]);
  // CORE-707 — one micro-batched lookup for every visible group-chat author.
  // Direct/AI threads have no per-message public identity and make no request.
  const { badges: messageBadges, statuses: messageBadgeStatuses } = useLevelBadgesState(
    group ? messages.map((message) => (message.isMine ? null : message.senderId)) : [],
  );
  // CORE-696 — «Пожаловаться» goes through the app-wide CORE-26 flow, the same
  // one the native menu uses, so no caller has to thread a handler down.
  const { openReport } = useReport();
  const reportMessage = useCallback(
    (m: ThreadMessage) => openReport({ targetType: 'message', targetId: String(m.id) }),
    [openReport],
  );
  // CORE-697 — row offsets, so tapping the pinned banner scrolls to the message
  // it is quoting rather than leaving the reader to hunt for it. The height goes
  // with it because the message menu renders INSIDE the row: without scrolling
  // the row's new bottom into view, the menu opens under the fold of a thread
  // that sits at its bottom — which is every thread you just wrote in, and every
  // message you just pinned. QA read that as «the menu no longer opens».
  const rowGeom = useRef<Record<string, { y: number; h: number }>>({});
  const listH = useRef(0);
  const scrollY = useRef(0);
  // CORE-1791 — читатель внизу ленты: когда поле растёт или открывается
  // палитра, лента становится ниже, и последнее сообщение уезжало под поле.
  const atBottom = useRef(true);
  const menuRow = useRef<string | null>(null);
  // CORE-1683 — a jump to a message the list did not hold yet: `onLoadAround`
  // brings its page in, the row measures itself on the next layout, and that
  // measurement is what scrolls. The auto-scroll-to-bottom effect stands down
  // while a jump is pending, else the fresh page would pull the reader to the
  // newest message instead of the one they asked for.
  const jumpTarget = useRef<string | null>(null);
  const jumpTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showAt = useCallback((id: string, y: number) => {
    scrollRef.current?.scrollTo({ y: Math.max(0, y - 12), animated: true });
    // CORE-1681 (QA 16.10) — на вебе лента чата растёт вместе со страницей
    // дашборда, и прокрутка внутреннего ScrollView ничего не двигает: сообщение
    // было в DOM на y≈970 при высоте окна 720, а страница оставалась наверху
    // профиля. `scrollIntoView` ведёт все прокручиваемые предки, включая окно.
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const node = document.getElementById(`chat-msg-${id}`);
      if (node?.scrollIntoView) {
        setTimeout(() => node.scrollIntoView({ block: 'center', behavior: 'smooth' }), 0);
      }
    }
    setHighlightId(id);
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlightId(null), 1800);
  }, []);
  useEffect(() => () => {
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    if (jumpTimer.current) clearTimeout(jumpTimer.current);
  }, []);
  // CORE-1682 — full-size view of an image attachment (thumbnail tap).
  const [viewer, setViewer] = useState<{ url: string; name?: string } | null>(null);
  const openImage = useCallback((url: string, name?: string) => setViewer({ url, name }), []);
  const closeImage = useCallback(() => setViewer(null), []);
  // CORE-1681 — «ссылка вела к сообщению, а его нет»: удалено или скрыто.
  // Висит дольше, чем грузится страница вокруг: человек успевает прочитать.
  const JUMP_NOTICE_MS = 10000;
  const [jumpNotice, setJumpNotice] = useState<string | null>(null);
  const jumpNoticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reportJumpMiss = useCallback(() => {
    setJumpNotice('Сообщение недоступно: оно удалено или скрыто.');
    if (jumpNoticeTimer.current) clearTimeout(jumpNoticeTimer.current);
    jumpNoticeTimer.current = setTimeout(() => setJumpNotice(null), JUMP_NOTICE_MS);
  }, []);
  useEffect(() => () => { if (jumpNoticeTimer.current) clearTimeout(jumpNoticeTimer.current); }, []);
  // CORE-1681 (QA 17.10) — уведомление стоит у композера, а по ссылке из
  // уведомлений страница дашборда открывается с шапки профиля: композер ниже
  // края окна, и объяснение никто бы не увидел. Подводим его так же, как само
  // сообщение в `showAt`.
  //
  // CORE-1681 (QA 19.10) — подводить надо после того, как уведомление
  // нарисовано: `setTimeout(0)` из колбэка срабатывал раньше коммита React,
  // узла `chat-jump-notice` ещё не было, и прокрутки не происходило —
  // текст оставался на y≈1014 при высоте окна 720. Эффект на само состояние
  // выполняется уже после коммита.
  useEffect(() => {
    if (!jumpNotice || Platform.OS !== 'web' || typeof document === 'undefined') return;
    const raf = requestAnimationFrame(() => {
      document.getElementById('chat-jump-notice')?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(raf);
  }, [jumpNotice]);
  const jumpToMessage = useCallback((id: string) => {
    const row = rowGeom.current[id];
    if (row) { showAt(id, row.y); return; }
    if (!onLoadAround) return;
    jumpTarget.current = id;
    if (jumpTimer.current) clearTimeout(jumpTimer.current);
    // The page may never bring the message (deleted meanwhile, hidden) — do not
    // hold the auto-scroll hostage for it.
    jumpTimer.current = setTimeout(() => { jumpTarget.current = null; }, 4000);
    void onLoadAround(id).then((found) => {
      if (found) return;
      jumpTarget.current = null;
      reportJumpMiss();
    });
  }, [onLoadAround, showAt, reportJumpMiss]);
  // CORE-1681 — the notification's message: one jump per id, after the first
  // page is on screen (rows measure on layout, hence the beat of delay — the
  // auto-scroll-to-bottom that follows a load has had its say by then).
  const focusedId = useRef<string | null>(null);
  useEffect(() => {
    if (!focusMessageId || loading) return;
    if (focusedId.current === focusMessageId) return;
    focusedId.current = focusMessageId;
    const t = setTimeout(() => jumpToMessage(focusMessageId), 150);
    return () => clearTimeout(t);
  }, [focusMessageId, loading, jumpToMessage]);
  const measureRow = useCallback((id: string, y: number, h: number) => {
    rowGeom.current[id] = { y, h };
    if (jumpTarget.current === id) {
      jumpTarget.current = null;
      if (jumpTimer.current) { clearTimeout(jumpTimer.current); jumpTimer.current = null; }
      showAt(id, y);
      return;
    }
    if (menuRow.current !== id || !listH.current) return;
    // The layout that just landed is the one WITH the menu in it.
    menuRow.current = null;
    const bottom = y + h;
    if (bottom <= scrollY.current + listH.current) return; // already visible
    // Keep the row's top on screen when the menu is taller than the list.
    const target = Math.max(0, Math.min(bottom - listH.current + 8, y));
    scrollRef.current?.scrollTo({ y: target, animated: true });
  }, [showAt]);
  const revealMenu = useCallback((id: string) => { menuRow.current = id; }, []);
  const [input, setInput] = useState('');
  // CORE-1685 — палитра комплекта под композером; вставка — в конец черновика.
  // CORE-1730 — после выбора палитра остаётся открытой: можно набрать несколько
  // подряд, закрывает её та же кнопка (решение владельца 21.10).
  const [emojiOpen, setEmojiOpen] = useState(false);
  // CORE-1791 (2b) — фокус внутри поля v3 усиливает его контур (.65 → 1).
  const [composerFocus, setComposerFocus] = useState(false);
  const pickEmoji = useCallback((type: string) => {
    // CORE-1791 — в поле с картинками эмодзи встаёт туда, где курсор.
    const field = draftFieldComposer ? (inputRef.current as unknown as DraftFieldHandle | null) : null;
    if (field?.insertToken) { field.insertToken(type); return; }
    setInput((prev) => insertEmojiToken(prev, type));
    inputRef.current?.focus();
  }, [draftFieldComposer]);
  // CORE-1790 — обычные эмодзи с клавиатуры уходят токенами комплекта;
  // закрытая наградная картинка остаётся Unicode, reward gate не обходится.
  const { access: reactionAccess } = useRewardReactions();
  const canUseReaction = useCallback((type: string) => reactionAccess(type) === 'open', [reactionAccess]);
  /** CORE-1350 (П3) — почему последняя отправка не дошла. `null` — всё в порядке. */
  const [sendNotice, setSendNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string } | null>(null);
  const [replying, setReplying] = useState<ReplyDraft | null>(null);
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const fileInputRef = useRef<any>(null);
  const keyRef = useRef(0);
  const scrollRef = useRef<ScrollView>(null);
  /** Человек только что отправил сообщение — следующую же перерисовку ленты
   *  доводим до низа, чтобы он увидел собственную реплику, а не её край. */
  const justSent = useRef(false);
  const inputRef = useRef<TextInput>(null);
  // CORE-311 — one line on mobile web, growing with the text.
  useComposerAutoGrow(inputRef, input, mobileComposer, MOBILE_INPUT_MIN_HEIGHT, MOBILE_INPUT_MAX_HEIGHT);

  const startEdit = useCallback((m: ThreadMessage) => {
    setEditing({ id: m.id });
    setInput(m.text);
    inputRef.current?.focus();
  }, []);

  const cancelEdit = useCallback(() => {
    setEditing(null);
    setInput('');
  }, []);

  const startReply = useCallback((m: ThreadMessage, fragment?: string) => {
    setEditing(null); // reply and edit are mutually exclusive
    setReplying({
      id: m.id,
      // CORE-1749 — над полем ровно то, о чём спросят: фраза в кавычках или
      // начало сообщения (у сообщения ИИ — без звёздочек разметки).
      text: quoteLine(
        { text: m.text, fragment, senderId: m.senderId },
        (t) => replyPreviewSource(t, m.attachments),
      ),
      senderName: m.senderName || '',
      mine: m.isMine,
      ...(fragment ? { fragment } : {}),
    });
    inputRef.current?.focus();
  }, []);

  // CORE-1749 — «Спросить об этом» у выделенной фразы (веб).
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const fragmentTextOf = useCallback(
    (id: string) => messagesRef.current.find((x) => x.id === id && !x.isMine)?.text ?? null,
    [],
  );
  const askAboutFragment = useCallback((id: string, fragment: string) => {
    const m = messagesRef.current.find((x) => x.id === id);
    if (m) startReply(m, fragment);
  }, [startReply]);

  const cancelReply = useCallback(() => setReplying(null), []);

  // ── Composer attachment upload (slice 5d-4) ──
  const stageUpload = useCallback(async (
    blob: Blob,
    opts: { name: string; isImage?: boolean; durationSec?: number; retryKey?: string },
  ) => {
    if (!onUpload) return;
    const key = opts.retryKey ?? `att-${keyRef.current++}`;
    const isImage = !!opts.isImage;
    if (opts.retryKey) {
      setPending((p) => p.map((it) => (it.key === key ? { ...it, status: 'uploading', refusal: undefined } : it)));
      setSendNotice(null);
    } else {
      const previewUrl = isImage && typeof URL !== 'undefined' ? URL.createObjectURL(blob) : undefined;
      setPending((p) => [...p, {
        key, name: opts.name || 'файл', status: 'uploading', previewUrl, isImage, blob, durationSec: opts.durationSec,
      }]);
    }
    // CORE-1356, возврат QA 25.09 — неудача загрузки должна быть СЛЫШНА.
    // Раньше она оставляла только значок на чипе: кнопка отправки при этом
    // выключена, пока вложение «загружается», и человек видел мёртвый интерфейс
    // без единого слова о том, что случилось и что делать.
    const failed = (why: string) => {
      setPending((p) => p.map((it) => (it.key === key ? { ...it, status: 'error', refusal: why } : it)));
      setSendNotice(why);
    };
    try {
      const att = await onUpload(blob, { name: opts.name, durationSec: opts.durationSec });
      if (att) {
        setPending((p) => p.map((it) =>
          it.key === key ? { ...it, status: 'done', attachment: att } : it));
      } else {
        failed(refusalText('', opts.name || 'файл'));
      }
    } catch (err: unknown) {
      // Отказ по правилам (тип, размер) полезнее общей фразы — он говорит, что
      // именно не так с этим файлом. Но `validateFile` отвечает по-английски и
      // служит разработчику, а не человеку: переводим два его случая, а
      // остальное показываем общей фразой.
      const raw = err instanceof Error ? err.message : '';
      // CORE-1682 — a refusal from the channel's own rules (format, size, the
      // server's code) is already in words; only the rest is translated here.
      const spoken = err instanceof Error && err.name === 'UploadRefusal';
      failed(spoken ? raw : refusalText(raw, opts.name || 'файл'));
    }
  }, [onUpload]);

  // CORE-1682 — «Повторить» on a failed chip: the file is still here.
  const retryPending = useCallback((key: string) => {
    const it = pending.find((x) => x.key === key);
    if (!it?.blob) return;
    void stageUpload(it.blob, { name: it.name, isImage: it.isImage, durationSec: it.durationSec, retryKey: key });
  }, [pending, stageUpload]);

  const onFilesPicked = useCallback((e: any) => {
    let files: File[] = Array.from(e?.target?.files || []);
    if (e?.target) e.target.value = ''; // let the same file be picked again
    // CORE-1682 — the per-message cap is enforced before any byte goes out:
    // the server would refuse the whole send, after every upload had finished.
    if (maxAttachments != null && maxAttachments > 0) {
      const room = Math.max(0, maxAttachments - pending.length);
      if (files.length > room) {
        setSendNotice(`Не больше ${maxAttachments} файлов в одном сообщении.`);
        files = files.slice(0, room);
      }
    }
    files.forEach((f) => void stageUpload(f, { name: f.name, isImage: (f.type || '').startsWith('image/') }));
  }, [stageUpload, maxAttachments, pending.length]);

  const removePending = useCallback((key: string) => {
    // CORE-311 (возврат QA 31.10) — отказ говорит об этом файле: файл убран,
    // отказ тоже. Остался другой отклонённый — говорим о нём.
    const refusal = pending.find((x) => x.key === key)?.refusal;
    if (refusal) {
      const next = pending.filter((x) => x.key !== key && x.refusal).pop()?.refusal ?? null;
      setSendNotice((cur) => (cur === refusal ? next : cur));
    }
    setPending((p) => {
      const it = p.find((x) => x.key === key);
      if (it?.previewUrl && typeof URL !== 'undefined') URL.revokeObjectURL(it.previewUrl);
      return p.filter((x) => x.key !== key);
    });
  }, [pending]);

  const clearPending = useCallback(() => {
    setPending((p) => {
      if (typeof URL !== 'undefined') p.forEach((x) => x.previewUrl && URL.revokeObjectURL(x.previewUrl));
      return [];
    });
  }, []);

  /**
   * CORE-1791 (возврат QA 31.10) — отказ отправки возвращает в поле не только
   * текст, но и загруженные файлы и цитату. Раньше они пропадали в момент
   * отправки, и повтор уходил одним текстом: файл терялся молча. Файл на
   * сервере уже лежит, загружать заново не нужно; превью картинки отозвано при
   * отправке и собирается заново из байтов. Приложенное человеком после
   * отправки остаётся, вернувшееся встаёт перед ним — как и текст; начатую
   * новую цитату не подменяем.
   */
  const restoreSent = useCallback((items: PendingAttachment[], reply: ReplyDraft | null) => {
    if (items.length) {
      const back = items.map((it) => ({
        ...it,
        previewUrl: it.isImage && it.blob && typeof URL !== 'undefined' ? URL.createObjectURL(it.blob) : undefined,
      }));
      setPending((p) => [...back.filter((it) => !p.some((x) => x.key === it.key)), ...p]);
    }
    if (reply) setReplying((cur) => cur ?? reply);
  }, []);

  // CORE-1791 — черновик принадлежит диалогу, а не экземпляру треда. Тред в
  // /chats один на все диалоги: без этого набранное для одного человека
  // оставалось в поле при переходе к другому, а файлы в очереди ушли бы не тому.
  // `draftOwner` — чей текст сейчас в поле. Сохранение объявлено раньше
  // загрузки: в коммите смены диалога `input` ещё прежний, и писать его под
  // новым ключом нельзя — владелец к этому моменту ещё старый, запись пропускается.
  const draftKey = surface?.draftKey ?? null;
  const draftsReady = !!surface?.draftsReady;
  const draftOwner = useRef<string | null>(null);
  useEffect(() => {
    if (!surface || editing || !draftOwner.current || draftOwner.current !== draftKey) return;
    surface.setDraft(draftOwner.current, input);
  }, [surface, draftKey, input, editing]);
  useEffect(() => {
    if (!surface || !draftKey || !draftsReady || draftOwner.current === draftKey) return;
    const switched = draftOwner.current !== null;
    draftOwner.current = draftKey;
    const stored = surface.getDraft(draftKey);
    // Первый вход: набранное до того, как хранилище дочиталось, не затираем.
    setInput((cur) => (switched ? stored : cur || stored));
    if (switched) {
      setEditing(null);
      setReplying(null);
      clearPending();
      setSendNotice(null);
    }
  }, [surface, draftKey, draftsReady, clearPending]);

  // ── Voice recording (slice 5d-4b, web + MediaRecorder) ──
  const [recording, setRecording] = useState(false);
  const [recordMs, setRecordMs] = useState(0);
  const mediaRef = useRef<any>(null);
  const chunksRef = useRef<Blob[]>([]);
  const micStreamRef = useRef<any>(null);
  const recTimerRef = useRef<any>(null);
  const recStartRef = useRef(0);
  const recCancelledRef = useRef(false);

  const canRecord = isWeb && allowVoice && !!onUpload
    && typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
    && typeof (globalThis as any).MediaRecorder !== 'undefined';

  const startRecording = useCallback(async () => {
    if (!canRecord) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;
      const MR: any = (globalThis as any).MediaRecorder;
      const mr = new MR(stream);
      chunksRef.current = [];
      recCancelledRef.current = false;
      mr.ondataavailable = (e: any) => { if (e.data?.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        micStreamRef.current?.getTracks?.().forEach((t: any) => t.stop());
        micStreamRef.current = null;
        if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; }
        const durationSec = Math.max(1, Math.round((Date.now() - recStartRef.current) / 1000));
        const recordedMime = mr.mimeType || 'audio/webm';
        // CORE-311 — Chrome records `audio/webm;codecs=opus`, and the attachment
        // limits list bare `audio/webm`: matched whole, the clip was refused as
        // «Такие файлы я пока не принимаю» before any upload.
        // CORE-1791 (возврат QA 23.10) — на любой ширине. Обрезка была только
        // при телефонном композере, и когда в разделе сообщений «телефон» стал
        // означать раскладку, а не ширину <768, голосовое на 701–767 начало
        // отклоняться; на desktop кнопка микрофона в разделе видна всегда и
        // была мёртвой. Параметр кодека для типа файла ничего не меняет.
        const mime = voiceUploadMime(recordedMime);
        const blob = new Blob(chunksRef.current, { type: mime });
        setRecording(false);
        setRecordMs(0);
        if (!recCancelledRef.current && blob.size) {
          void stageUpload(blob, {
            name: `Голосовое сообщение.${mime.includes('mp4') || mime.includes('mpeg') ? 'mp4' : 'webm'}`,
            durationSec,
          });
        }
      };
      mediaRef.current = mr;
      recStartRef.current = Date.now();
      mr.start();
      setRecording(true);
      recTimerRef.current = setInterval(() => setRecordMs(Date.now() - recStartRef.current), 200);
    } catch {
      // permission denied / unsupported — leave the composer as-is.
      setRecording(false);
    }
  }, [canRecord, stageUpload]);

  const stopRecording = useCallback((cancel: boolean) => {
    recCancelledRef.current = cancel;
    try { mediaRef.current?.stop(); } catch { /* already stopped */ }
  }, []);

  // Stop the mic + timers if the thread unmounts mid-recording.
  useEffect(() => () => {
    try { mediaRef.current?.stop(); } catch { /* noop */ }
    micStreamRef.current?.getTracks?.().forEach((t: any) => t.stop());
    if (recTimerRef.current) clearInterval(recTimerRef.current);
  }, []);

  // CORE-1653 — упоминания людей: в поле `@Имя`, в отправленном тексте метка
  // с id. Возврат текста с метками в поле (правка своего сообщения, отказ
  // отправки) хук разворачивает обратно сам.
  const mentionDraft = useMentionDraft(input, setInput, mentionCandidates, inputRef, searchMentions);
  const serializeDraft = mentionDraft.serialize;

  const mentionQuery = mentionables.length > 0 ? activeMention(input) : null;
  const mentionMatches =
    mentionQuery === null
      ? []
      : mentionables
          .filter((m) => m.name.toLowerCase().startsWith(mentionQuery.toLowerCase()))
          .slice(0, 6);

  const pickMention = useCallback((name: string) => {
    // Replace the trailing `@partial` with `@Name `.
    setInput((prev) => prev.replace(/(?:^|\s)@[^\s@]*$/, (match) => `${match.startsWith(' ') ? ' ' : ''}@${name} `));
    inputRef.current?.focus();
  }, []);

  // Auto-scroll to the newest message / while the peer is "thinking".
  useEffect(() => {
    // CORE-1683 — a page pulled in for a jump is not «new messages».
    if (jumpTarget.current) return;
    const id = setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 80);
    return () => clearTimeout(id);
  }, [messages.length, thinking]);

  /**
   * CORE-1791 (возврат QA 28.10) — отправка, текст которой вернулся в поле:
   * ключ повтора и текст. В разделе сообщений — у черновика своего диалога
   * (переживает переход в другой диалог и перезагрузку), вне его — здесь.
   */
  const localAttempt = useRef<ChatSendAttempt | null>(null);
  const attemptOf = useCallback(
    (owner: string | null): ChatSendAttempt | null =>
      (owner && surface?.getSendAttempt ? surface.getSendAttempt(owner) : localAttempt.current),
    [surface],
  );
  const rememberAttempt = useCallback((owner: string | null, attempt: ChatSendAttempt | null) => {
    if (owner && surface?.setSendAttempt) surface.setSendAttempt(owner, attempt);
    else localAttempt.current = attempt;
  }, [surface]);
  /** Подсказка, показанная из-за этой отправки: её и снимаем, когда сообщение дошло. */
  const attemptNotice = useRef<{ clientMessageId: string; message: string } | null>(null);

  /** Отказ своей отправки — в поле этого треда: подсказка, текст, файлы, цитата. */
  const takeRefusal = useCallback((r: Refusal) => {
    attemptNotice.current = { clientMessageId: r.clientMessageId, message: r.message };
    setSendNotice(r.message);
    if (r.text) setInput((cur) => (cur ? `${r.text}\n${cur}` : r.text));
    restoreSent(r.items, r.reply);
  }, [restoreSent]);
  useEffect(() => {
    const owner = draftOwner.current;
    if (!owner || owner !== draftKey) return;
    shownThreads.set(owner, takeRefusal);
    return () => { if (shownThreads.get(owner) === takeRefusal) shownThreads.delete(owner); };
  }, [draftKey, draftsReady, takeRefusal]);

  /**
   * CORE-1350 (П3) — что делать с исходом отправки.
   *
   * Поле по-прежнему чистится сразу: отправка почти всегда проходит, и держать
   * текст до ответа сервера значило бы замедлить нормальный случай ради
   * редкого. Поэтому текст не удерживается, а ВОЗВРАЩАЕТСЯ на отказе.
   *
   * `setInput` вызывается функциональной формой не для красоты: `setInput('')`
   * в `submit` уже поставлен в очередь к этому моменту, и обычная форма записала
   * бы возврат поверх пустоты в неизвестном порядке. Если человек успел набрать
   * новое — прежнее встаёт сверху отдельной строкой: перезаписывать набранное
   * ради возврата значит потерять текст ровно тем способом, от которого чиним.
   *
   * CORE-1791 (возврат QA 28.10) — текст возвращается и на сорванном по
   * времени вызове (`unconfirmed`). Раньше его не возвращали, потому что сервер
   * мог сообщение принять и повтор дал бы дубль; теперь повтор идёт с тем же
   * ключом (`clientMessageId`), и второй копии сервер не создаст. Если же
   * задержанная отправка дошла, она приедет с историей, и подсказку вместе с
   * текстом уберёт эффект ниже.
   *
   * Отказ принадлежит диалогу, из которого отправляли, а не треду: пока ждали
   * ответа, человек мог открыть другой диалог, а тред при этом снимается и
   * собирается заново (между диалогами экран проходит через «Открываем…»,
   * Агапелла — другая панель). Поэтому отказ получает тот тред, который
   * сейчас показывает свой диалог (`shownThreads`), — и этот же, и новый. Если
   * диалог не открыт нигде, текст уходит в его черновик, а файлы и цитата ждут
   * возврата к нему (`returnedSends`).
   */
  const handleSendResult = useCallback(
    (
      sent: ChatSendAttempt & { owner: string | null; items: PendingAttachment[]; reply: ReplyDraft | null },
      result: void | Promise<SendResult | void>,
    ) => {
      // Источник, которому сказать нечего (круги, события, DM), возвращает
      // `void` — исход считается успешным, как было до правки.
      if (!result || typeof (result as Promise<unknown>).then !== 'function') return;
      void Promise.resolve(result).then((r) => {
        if (!r || r.outcome === 'sent') return;
        const { owner, items, reply, ...attempt } = sent;
        const { text, clientMessageId } = attempt;
        if (text || items.length) rememberAttempt(owner, attempt);
        const returned: ReturnedSend = { clientMessageId, message: r.message, items, reply };
        // Вне раздела сообщений диалог у треда один — он и принимает.
        const shown = owner === null ? takeRefusal : shownThreads.get(owner);
        if (shown) {
          shown({ ...returned, text });
          return;
        }
        if (text && owner && surface) {
          const cur = surface.getDraft(owner);
          surface.setDraft(owner, cur ? `${text}\n${cur}` : text);
        }
        if (owner) returnedSends.set(owner, returned);
      });
    },
    [rememberAttempt, surface, takeRefusal],
  );

  /**
   * CORE-1791 (возврат QA 28.10) — задержанная отправка всё-таки дошла: своё
   * сообщение с ключом этой попытки приехало (с историей после переподключения
   * или живым событием). Подсказка «не отправлено» больше неправда, а текст в
   * поле приглашает отправить его второй раз — убираем и то и другое. Текст —
   * только если человек его не менял; набранное поверх остаётся.
   */
  useEffect(() => {
    const owner = draftOwner.current;
    const attempt = attemptOf(owner);
    if (!attempt || !attemptDelivered(messages, attempt)) return;
    rememberAttempt(owner, null);
    const notice = attemptNotice.current;
    if (notice && notice.clientMessageId === attempt.clientMessageId) {
      attemptNotice.current = null;
      setSendNotice((cur) => (cur === notice.message ? null : cur));
    }
    // CORE-1791 (возврат QA 31.10) — файлы этой отправки уже в дошедшем
    // сообщении: вернувшиеся карточки убираем всегда, иначе следующая отправка
    // приложит их второй раз. Цитату — вместе с текстом.
    if (attempt.attachments?.length) {
      setPending((p) => {
        const sent = p.filter((x) => isAttemptFile(attempt, x.attachment));
        if (!sent.length) return p;
        if (typeof URL !== 'undefined') sent.forEach((x) => x.previewUrl && URL.revokeObjectURL(x.previewUrl));
        return p.filter((x) => !sent.includes(x));
      });
    }
    if (!editing && brandifyEmoji(serializeDraft().trim(), canUseReaction) === attempt.text) {
      setInput('');
      if (attempt.replyTo) setReplying((cur) => (cur?.id === attempt.replyTo ? null : cur));
    }
  }, [messages, draftKey, draftsReady, attemptOf, rememberAttempt, editing, serializeDraft, canUseReaction]);

  /**
   * CORE-1791 (возврат QA 31.10) — диалог открыт, а у него есть неотправленное
   * сообщение. Отказ пришёл, пока диалог не был открыт, — его причина, файлы и
   * цитата ждут в `returnedSends` и возвращаются. Если же файлов в поле нет и
   * взять их неоткуда (перезагрузка, новая вкладка, карточки убраны вручную),
   * говорим об этом один раз, до повтора: иначе повтор уйдёт одним текстом, и
   * файл пропадёт молча.
   */
  const openedFor = useRef<string | null>(null);
  useEffect(() => {
    const owner = draftOwner.current;
    if (!owner || owner !== draftKey || openedFor.current === owner) return;
    openedFor.current = owner;
    const back = returnedSends.get(owner);
    returnedSends.delete(owner);
    const attempt = attemptOf(owner);
    const step = reopenStep(attempt, back?.clientMessageId, filesNoticed);
    if (!step || !attempt) return;
    // Текст уже в поле из черновика; вернуть причину, файлы и цитату.
    if (step === 'restore' && back) restoreSent(back.items, back.reply);
    else filesNoticed.add(attempt.clientMessageId);
    const message = step === 'restore' && back ? back.message : FILES_NOT_KEPT;
    attemptNotice.current = { clientMessageId: attempt.clientMessageId, message };
    setSendNotice(message);
  }, [draftKey, draftsReady, attemptOf, restoreSent]);

  const submit = useCallback(
    (text: string) => {
      // Быстрые ответы приходят готовым текстом; набранное в поле — с метками.
      const typed = (text === input ? serializeDraft() : text).trim();
      // Прошлый отказ к новой попытке отношения не имеет.
      setSendNotice(null);
      attemptNotice.current = null;
      // CORE-1790 — эмодзи с клавиатуры уходят токенами комплекта.
      const trimmed = brandifyEmoji(typed, canUseReaction);
      if (editing) {
        // Edits are text-only — no attachment staging.
        if (!trimmed) return;
        onEdit?.(editing.id, trimmed);
        setEditing(null);
      } else {
        const items = pending.filter((x) => x.status === 'done' && x.attachment);
        const ready = items.map((x) => x.attachment as ComposerAttachment);
        if (!trimmed && ready.length === 0) return;
        // CORE-1791 — то же, что вернулось в поле после неудачи (текст, файлы,
        // цитата), — та же попытка и тот же ключ; что-то другое — новая.
        const owner = draftOwner.current;
        const prior = attemptOf(owner);
        const content: SendContent = {
          text: trimmed,
          ...(ready.length ? { attachments: ready.map(attachmentSignature) } : {}),
          ...(replying ? { replyTo: replying.id } : {}),
        };
        const clientMessageId = retryKeyFor(prior, content);
        if (prior) rememberAttempt(owner, null);
        // CORE-1749 — четвёртым — выделенная фраза, если отвечают на неё.
        handleSendResult(
          { ...content, clientMessageId, owner, items, reply: replying },
          onSend(trimmed, replying?.id, ready.length ? ready : undefined, replying?.fragment, clientMessageId),
        );
        setReplying(null);
        clearPending();
        // Своё отправленное сообщение обязано оказаться на виду. Флаг, а не
        // таймер: сообщение появляется в ленте не сразу (оптимистично либо
        // ответом сервера), и любая выбранная задержка либо коротка, либо
        // дёргает ленту впустую. Догоняет `onContentSizeChange` — ровно тогда,
        // когда новая высота уже посчитана.
        justSent.current = true;
      }
      setInput('');
      inputRef.current?.focus(); // keep focus after send (CORE-375 parity)
    },
    [onSend, onEdit, editing, replying, pending, clearPending, handleSendResult, input, serializeDraft, canUseReaction, attemptOf, rememberAttempt],
  );

  const uploading = pending.some((x) => x.status === 'uploading');
  const hasReadyAttachment = pending.some((x) => x.status === 'done');
  // CORE-1682 — a failed upload blocks the send: a message that went out
  // without one of its files would read as «sent» while the file was not.
  const failedAttachment = pending.some((x) => x.status === 'error');
  const canSend = editing
    ? !!input.trim()
    : (!!input.trim() || hasReadyAttachment) && !uploading && !failedAttachment;

  // CORE-86 — the chip bar belongs to the current turn: it is suppressed when
  // that turn already offers inline highlights or content cards (one way to
  // choose per turn), and it goes away once the user has answered in their own
  // words.
  const quickReplies = selectTurnQuickReplies(messages);

  // CORE-1791 (возврат QA 23.10) — имя кнопки — её действие. Прежнее «⌘ + Enter —
  // отправить» обещало сочетание, а отправляет просто Enter (Shift + Enter —
  // новая строка); клавиши — в подсказке при наведении.
  const sendLabel = tChats('chat.send');
  const sendHint = tChats('chat.sendHint');

  // `headerSlot` is chrome that belongs to the screen, not to the message list —
  // e.g. the «Пройти знакомство с платформой» CTA (CORE-496). It used to render
  // only on the success path, so whenever the thread failed to load the user was
  // left with an error and no way out: the one action that still worked
  // disappeared exactly when they needed it. Keep it on every branch.
  if (loading) {
    return (
      <View style={styles.root}>
        {headerSlot}
        <View style={styles.center}>
          <ActivityIndicator size="large" color={tc.solar} />
        </View>
      </View>
    );
  }
  if (error) {
    return (
      <View style={styles.root}>
        {headerSlot}
        <View style={styles.center}>
          {v3 ? (
            // CORE-1791 — та же форма ошибки, что над полем: значок alert и терракотовый текст.
            <View style={[styles.sendNotice, { alignItems: 'center' }]}>
              <MessagesIcon name="alert" size={16} color={v3.t.error} />
              <Text style={styles.errorText} accessibilityRole="alert">Не удалось открыть чат. Попробуйте ещё раз.</Text>
            </View>
          ) : (
            <>
              <Ionicons name="chatbubble-ellipses-outline" size={44} color="rgba(232,93,4,0.3)" />
              <Text style={styles.errorText}>Не удалось открыть чат. Попробуйте ещё раз.</Text>
            </>
          )}
        </View>
      </View>
    );
  }

  // CORE-1791 — ошибка отличается формой: контурный alert из набора раздела + терракотовый текст.
  const sendNoticeIcon = v3
    ? <MessagesIcon name="alert" size={16} color={v3.t.error} testID="chat-send-notice-icon" />
    : <Ionicons name="alert-circle-outline" size={15} color={tc.error} accessible={false} />;

  // CORE-311 — above the composer on desktop, inside the rounded field on mobile web.
  const stagedStrip = pending.length > 0 && (
    <View style={[styles.stagedStrip, mobileComposer && mStyles.stagedStrip]} testID="chat-staged-attachments">
      {pending.map((it) => (
        <View key={it.key} style={styles.stagedItem}>
          {it.isImage && it.previewUrl ? (
            <Image source={{ uri: it.previewUrl }} style={styles.stagedThumb} />
          ) : (
            <View style={[styles.stagedThumb, styles.stagedFile]}>
              <Ionicons name="document" size={20} color={tc.textSecondary} />
              <Text style={styles.stagedName} numberOfLines={1}>{it.name}</Text>
            </View>
          )}
          {it.status === 'uploading' && (
            <View style={styles.stagedOverlay}><ActivityIndicator size="small" color="#fff" /></View>
          )}
          {it.status === 'error' && (
            <Pressable
              style={[styles.stagedOverlay, styles.stagedErrorOverlay]}
              onPress={it.blob ? () => retryPending(it.key) : undefined}
              accessibilityLabel="Повторить загрузку"
              testID="chat-staged-retry"
              {...(typeof document !== 'undefined' ? ({ title: 'Повторить загрузку' } as object) : {})}
            >
              {v3 && !it.blob
                ? <MessagesIcon name="alert" size={18} color={v3.t.error} />
                : <Ionicons name={it.blob ? 'refresh' : 'alert-circle'} size={18} color={v3 ? v3.t.error : '#fff'} />}
              {!!it.blob && <Text style={styles.stagedRetryText}>Повторить</Text>}
            </Pressable>
          )}
          <Hit
            hit={hs ? [hs.stagedRemoveHit, mobileComposer && hs.stagedRemoveHitPhone] : null}
            visual={[styles.stagedRemove, mobileComposer && mStyles.stagedRemove, hs?.stagedRemoveVisual]}
            onPress={() => removePending(it.key)}
            accessibilityLabel="Убрать вложение"
            hitSlop={6}
          >
            <Ionicons name="close" size={mobileComposer ? 14 : 12} color="#fff" />
          </Hit>
        </View>
      ))}
    </View>
  );

  // CORE-311 — before the input on desktop, at the end of the rounded field on mobile web.
  const emojiButton = v3 ? (
    <V3Button
      visual={styles.emojiBtn}
      hover={styles.iconBtnHover}
      still={v3.reduceMotion}
      onPress={() => setEmojiOpen((v) => !v)}
      accessibilityRole="button"
      accessibilityLabel="Эмодзи"
      accessibilityState={{ expanded: emojiOpen }}
      testID="chat-emoji-button"
      // CORE-1730 — кнопка не забирает фокус у поля: клавиатура телефона остаётся.
      {...(typeof document !== 'undefined'
        ? ({ title: 'Эмодзи', onMouseDown: (e: { preventDefault(): void }) => e.preventDefault() } as object)
        : {})}
    >
      <MessagesIcon name="smile" size={v3.g.composerButtonGlyph} color={v3.t.accent} />
    </V3Button>
  ) : (
    <Pressable
      style={[styles.emojiBtn, mobileComposer && mStyles.fieldBtn, hs?.square]}
      onPress={() => setEmojiOpen((v) => !v)}
      accessibilityLabel="Эмодзи"
      accessibilityState={{ expanded: emojiOpen }}
      testID="chat-emoji-button"
      // CORE-1730 — тап по кнопке уводил фокус из поля, и телефон прятал
      // клавиатуру; mousedown без действия по умолчанию фокус не трогает.
      {...(typeof document !== 'undefined'
        ? ({ title: 'Эмодзи', onMouseDown: (e: { preventDefault(): void }) => e.preventDefault() } as object)
        : {})}
    >
      <Ionicons name={emojiOpen ? 'close-outline' : 'happy-outline'} size={22} color={emojiOpen ? tc.solar : tc.textSecondary} />
    </Pressable>
  );

  return (
    <View style={styles.root} testID="agapella-chat-thread">
      {(onBack || onPressPeer) && (
        <View style={styles.backHeader} testID="chat-thread-header">
          {/* CORE-1791 (2b) — нижняя линия шапки v3: тёплый градиент поверх границы. */}
          {v3 && (
            <View
              pointerEvents="none"
              style={{ position: 'absolute', left: 0, right: 0, bottom: -1, height: 1, ...({ backgroundImage: 'linear-gradient(90deg,rgba(255,113,56,.12),rgba(255,154,98,.36),rgba(246,197,167,.05))' } as object) }}
            />
          )}
          {onBack && (
            <Pressable
              onPress={onBack}
              hitSlop={12}
              accessibilityRole="button"
              accessibilityLabel="Назад к списку"
              style={[styles.backHeaderBtn, hs?.backHeaderBtn, v3 && { marginLeft: -4, marginVertical: 0, borderRadius: 12 }]}
            >
              {v3
                ? <MessagesIcon name="back" size={22} color={v3.t.accent} />
                : <Ionicons name="arrow-back" size={24} color={tc.textPrimary} />}
            </Pressable>
          )}
          {/* Peer block — a button when onPressPeer is set (human DM), opening
              the interlocutor's public profile. Inert otherwise. CORE-480. */}
          {(() => {
            const PeerWrap: any = onPressPeer ? Pressable : View;
            const peerWrapProps: any = onPressPeer
              ? {
                  onPress: onPressPeer,
                  accessibilityRole: 'button',
                  accessibilityLabel: `Открыть профиль: ${peer.name}`,
                  ...(Platform.OS === 'web' ? { style: { cursor: 'pointer' } } : {}),
                }
              : {};
            // CORE-1791 (2b) — блок собеседника — кнопка профиля: не ниже 44 и на телефоне.
            const peerHit = v3 ? { minHeight: 44 } : null;
            return (
              // Spread first: peerWrapProps.style (web cursor) must merge into the
              // row layout below, not replace it — otherwise the DM header stacks
              // the name under the avatar. CORE-311.
              <PeerWrap
                {...peerWrapProps}
                style={{ flex: 1, minWidth: 0, flexDirection: 'row', alignItems: 'center', gap: v3 ? v3.g.headerGap : 10, ...peerHit, ...(peerWrapProps.style || {}) }}
              >
                {v3 ? (
                  // CORE-1791 (2b) — аватар шапки v3: компактная подложка, тонкий
                  // контур .65; без фото — инициалы акцентом (не одна буква на оранжевом).
                  <View style={{ position: 'relative', width: v3.g.headerAvatar, height: v3.g.headerAvatar }}>
                    {peer.brandedAvatar ? (
                      <Image source={peer.brandedAvatar} style={{ width: v3.g.headerAvatar, height: v3.g.headerAvatar, borderRadius: v3.g.headerAvatar / 2 }} />
                    ) : peer.avatarUrl ? (
                      <Image source={{ uri: peer.avatarUrl }} style={{ width: v3.g.headerAvatar, height: v3.g.headerAvatar, borderRadius: v3.g.headerAvatar / 2 }} accessibilityIgnoresInvertColors />
                    ) : (
                      <View style={{ width: v3.g.headerAvatar, height: v3.g.headerAvatar, borderRadius: v3.g.headerAvatar / 2, alignItems: 'center', justifyContent: 'center', backgroundColor: v3.t.initialsBase ?? 'transparent', ...({ backgroundImage: v3.t.softWell, boxShadow: v3.t.headerAvatarShadow } as object) }}>
                        <Text style={{ color: v3.t.accent, fontSize: v3.phone ? 12 : 14, fontWeight: '700', fontFamily: 'var(--ag-font-ui)' } as any}>{initialsOf(peer.name)}</Text>
                      </View>
                    )}
                    <ContourRing radius={v3.g.headerAvatar / 2} opacity={CONTOUR_OPACITY.avatar} />
                  </View>
                ) : peer.brandedAvatar ? (
                  <Image source={peer.brandedAvatar} style={styles.backHeaderAvatar} />
                ) : peer.avatarUrl ? (
                  <Image source={{ uri: peer.avatarUrl }} style={styles.backHeaderAvatar} />
                ) : (
                  <View style={[styles.backHeaderAvatar, styles.avatarFallback]}>
                    <Text style={styles.avatarLetter}>{(peer.name || '?').charAt(0)}</Text>
                  </View>
                )}
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={[styles.backHeaderName, v3?.phone && { maxWidth: 145 }]} numberOfLines={1}>{peer.name}</Text>
                  {!!peerSubtitle && (v3 ? (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 2 }}>
                      {peerOnline && <View style={styles.presenceDot} />}
                      <Text style={[styles.backHeaderSub, { marginTop: 0 }]} numberOfLines={1}>{peerSubtitle}</Text>
                    </View>
                  ) : (
                    <Text style={[styles.backHeaderSub, peerOnline && { color: '#3DD66B' }]} numberOfLines={1}>
                      {peerSubtitle}
                    </Text>
                  ))}
                </View>
              </PeerWrap>
            );
          })()}
        </View>
      )}
      {headerSlot}
      {!!pinned && (
        <Pressable
          style={[styles.pinnedBar, hs?.minRow]}
          onPress={() => {
            const row = rowGeom.current[pinned.messageId];
            if (row) scrollRef.current?.scrollTo({ y: Math.max(0, row.y - 12), animated: true });
          }}
          accessibilityLabel="Перейти к закреплённому сообщению"
        >
          <Ionicons name="pin" size={14} color={tc.solar} style={{ transform: [{ rotate: '45deg' }] }} />
          <View style={{ flex: 1 }}>
            <Text style={styles.pinnedBarTitle}>Закреплённое сообщение</Text>
            {!!pinned.text && (
              <Text style={styles.pinnedBarText} numberOfLines={1}>
                <EmojiText text={mentionsToPlainText(pinned.text)} size={14} />
              </Text>
            )}
          </View>
        </Pressable>
      )}
      <ScrollView
        ref={scrollRef}
        style={styles.list}
        contentContainerStyle={styles.listContent}
        onLayout={(e) => {
          const h = e.nativeEvent.layout.height;
          const shrank = h < listH.current - 1;
          listH.current = h;
          // CORE-1791 — лента стала ниже (поле выросло, открылась палитра): кто
          // был внизу, остаётся внизу. Кто читает выше — того не дёргаем.
          if (surface && shrank && atBottom.current) scrollRef.current?.scrollToEnd({ animated: false });
        }}
        onScroll={(e) => {
          const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
          scrollY.current = contentOffset.y;
          atBottom.current = contentOffset.y + layoutMeasurement.height >= contentSize.height - 32;
        }}
        onContentSizeChange={() => {
          // Только после собственной отправки: подгрузка истории и чужие
          // сообщения не должны выдёргивать человека из места, которое он читает.
          if (!justSent.current) return;
          justSent.current = false;
          scrollRef.current?.scrollToEnd({ animated: true });
        }}
        scrollEventThrottle={16}
      >
        {messages.map((m, i) => {
          const prev = i > 0 ? messages[i - 1] : null;
          const showDate = !prev || !sameDay(prev.createdAt, m.createdAt);
          const label = showDate ? dayLabel(m.createdAt) : '';
          return (
            <React.Fragment key={m.id}>
              {!!label && (
                <View style={styles.dateSep}>
                  {v3 && <View style={{ height: 1, width: 70, backgroundColor: v3.t.border }} />}
                  <Text style={styles.dateSepText}>{label}</Text>
                  {v3 && <View style={{ height: 1, width: 70, backgroundColor: v3.t.border }} />}
                </View>
              )}
              <MessageRow
                m={m}
                peer={peer}
                s={styles}
                tc={tc}
                onReact={onReact}
                onEditStart={onEdit ? startEdit : undefined}
                onDelete={onDelete}
                onReply={onSend}
                onReplyStart={enableReply ? startReply : undefined}
                onPressQuote={jumpToMessage}
                fragmentSource={enableReply && enableFragmentReply && !m.isMine}
                highlighted={highlightId === m.id}
                onOpenImage={openImage}
                onBlock={onBlock}
                blockAction={blockAction}
                onReport={reportMessage}
                isPinned={!!pinned && pinned.messageId === m.id}
                onPinToggle={onPinToggle}
                onMarkUnread={onMarkUnread}
                onMeasure={measureRow}
                onMenuOpen={revealMenu}
                group={group}
                senderBadge={m.senderId ? messageBadges[m.senderId] : null}
                senderBadgeStatus={m.senderId ? (messageBadgeStatuses[m.senderId] ?? 'disabled') : 'disabled'}
                renderSpecial={renderSpecial}
                myUserId={myUserId}
                onPressUser={onPressUser}
                canDeleteAny={canDeleteAny}
                mobileWeb={mobileComposer}
                hs={hs}
                v3={v3}
              />
            </React.Fragment>
          );
        })}
        {thinking && (
          <View style={[styles.row, styles.rowTheirs]}>
            <View style={[styles.bubble, styles.bubbleTheirs]}>
              <Text style={styles.thinkingText}>{peer.name} печатает…</Text>
            </View>
          </View>
        )}
      </ScrollView>
      {enableReply && enableFragmentReply && isWeb && (
        <FragmentAskButton textOf={fragmentTextOf} onAsk={askAboutFragment} />
      )}

      {quickReplies.length > 0 && (
        <View style={styles.quickReplies} testID="quick-replies">
          {quickReplies.map((q, i) => (
            <Hit key={`${q}-${i}`} hit={hs?.chipHit ?? null} visual={styles.chip} onPress={() => submit(q)} testID="quick-reply-btn">
              <Text style={styles.chipText}>{q}</Text>
            </Hit>
          ))}
        </View>
      )}

      {mentionMatches.length > 0 && (
        <View style={styles.mentionMenu}>
          {mentionMatches.map((m) => (
            <Pressable key={m.id} style={[styles.mentionItem, hs?.minRow]} onPress={() => pickMention(m.name)}>
              <View style={[styles.avatar, styles.avatarFallback, styles.mentionAvatar]}>
                <Text style={styles.avatarLetter}>{(m.name || '?').charAt(0)}</Text>
              </View>
              <Text style={styles.mentionName}>{m.name}</Text>
            </Pressable>
          ))}
        </View>
      )}

      {(mentionDraft.matches.length > 0 || mentionDraft.showEmpty) && (
        <View style={styles.mentionMenu} testID="chat-mention-suggestions" accessibilityRole="menu">
          {mentionDraft.matches.length === 0 ? (
            <Text style={styles.mentionEmpty}>
              {searchMentions ? 'Никого не нашлось по такому имени или нику' : 'В этом чате нет участника с таким именем'}
            </Text>
          ) : mentionDraft.matches.map((c) => (
            <Pressable
              key={c.userId}
              testID="chat-mention-option"
              style={[styles.mentionItem, hs?.minRow]}
              onPress={() => mentionDraft.pick(c)}
              accessibilityRole="menuitem"
              accessibilityLabel={`Упомянуть ${c.name}`}
            >
              {c.avatar ? (
                <Image source={{ uri: c.avatar }} style={[styles.avatar, styles.mentionAvatar]} />
              ) : (
                <View style={[styles.avatar, styles.avatarFallback, styles.mentionAvatar]}>
                  <Text style={styles.avatarLetter}>{(c.name || '?').charAt(0)}</Text>
                </View>
              )}
              <Text style={styles.mentionName} numberOfLines={1}>{c.name}</Text>
              {(c.isHost || !!c.tag || !!c.nickname) && (
                <Text style={styles.mentionMeta} numberOfLines={1} testID="chat-mention-meta">
                  {[c.nickname ? `@${c.nickname}` : null, c.isHost ? 'Мастер' : null, c.tag].filter(Boolean).join(' · ')}
                </Text>
              )}
            </Pressable>
          ))}
        </View>
      )}

      {muted ? (
        // Mute gate (slice 5d-3): the composer is replaced by a hint while the
        // current user is muted in this circle — parity with the Stream path.
        <View style={styles.mutedBar} testID="chat-muted-hint">
          <Ionicons name="volume-mute" size={18} color={tc.textSecondary} />
          <Text style={styles.mutedText} numberOfLines={2}>
            {muteHint || 'Вы сейчас не можете писать в этот круг.'}
          </Text>
        </View>
      ) : (
        <ComposeWrap style={v3 ? styles.composeArea : null}>
          {replying && !editing && (
            <View style={styles.editBanner} testID="chat-reply-banner">
              {/* CORE-1791 — значимый глиф: акцентный токен (3:1+), фирменный #E98245 на светлой плашке давал 2.4. */}
              <Ionicons name="arrow-undo-outline" size={15} color={v3 ? v3.t.accent : tc.solar} />
              <View style={styles.replyBannerBody}>
                <Text style={styles.replyBannerName} numberOfLines={1}>
                  {/* CORE-1791 — в разделе ответ на своё подписан «Вы», как и цитата в пузыре. */}
                  {v3 && replying.mine ? tChats('sidebar.you') : (replying.senderName || 'Сообщение')}
                </Text>
                {/* CORE-1749 — фраза важнее начала сообщения: ей две строки. */}
                <Text style={styles.replyBannerText} numberOfLines={replying.fragment ? 2 : 1} testID="chat-reply-banner-text">
                  <EmojiText text={replying.text} size={14} />
                </Text>
              </View>
              <Pressable onPress={cancelReply} accessibilityLabel="Отменить" hitSlop={8} style={hs?.bannerClose}>
                <Ionicons name="close" size={18} color={tc.textMuted} />
              </Pressable>
            </View>
          )}

          {editing && (
            <View style={styles.editBanner}>
              <Ionicons name="pencil" size={15} color={v3 ? v3.t.accent : tc.solar} />
              <Text style={styles.editBannerText} numberOfLines={1}>Редактирование сообщения</Text>
              <Pressable onPress={cancelEdit} accessibilityLabel="Отменить" hitSlop={8} style={hs?.bannerClose}>
                <Ionicons name="close" size={18} color={tc.textMuted} />
              </Pressable>
            </View>
          )}

          {(!mobileComposer || !!v3) && stagedStrip}
          {pending.length > 0 && !!attachmentHint && (
            <Text style={styles.attachHint} testID="chat-attachment-hint">{attachmentHint}</Text>
          )}
          {failedAttachment && !editing && (v3 ? (
            <View style={styles.sendNotice}>
              <MessagesIcon name="alert" size={16} color={v3.t.error} />
              <Text style={styles.attachBlocked} testID="chat-attachment-blocked">
                Файл не загрузился. Повторите загрузку или уберите его — иначе сообщение не отправится.
              </Text>
            </View>
          ) : (
            <Text style={styles.attachBlocked} testID="chat-attachment-blocked">
              Файл не загрузился. Повторите загрузку или уберите его — иначе сообщение не отправится.
            </Text>
          ))}
          {emojiOpen && !editing && (v3 ? (
            // CORE-1791 (2b) — палитра v3 всплывает над полем, не сдвигая ленту.
            <View style={styles.emojiPicker} testID="chat-emoji-picker">
              <EmojiStrip onPick={pickEmoji} testID="chat-emoji-strip" style={{ width: 'auto' }} />
            </View>
          ) : <EmojiStrip onPick={pickEmoji} testID="chat-emoji-strip" />)}

          {/*
            CORE-1350 (П3) — отказ отправки виден человеку.
            Стоит над композером, а не поверх ленты: смотрят сюда, и возвращённый
            текст оказывается прямо под объяснением, почему он вернулся.
          */}
          {!!sendNotice && (
            <View style={styles.sendNotice} testID="chat-send-notice">
              {sendNoticeIcon}
              <Text accessibilityRole="alert" style={styles.sendNoticeText}>{sendNotice}</Text>
            </View>
          )}
          {!!jumpNotice && (
            <View style={styles.sendNotice} testID="chat-jump-notice" nativeID="chat-jump-notice">
              <Ionicons name="information-circle-outline" size={15} color={tc.textSecondary} accessible={false} />
              <Text accessibilityRole="alert" style={[styles.sendNoticeText, { color: tc.textSecondary }]}>{jumpNotice}</Text>
            </View>
          )}

          {composerTopSlot}

          {recording ? (
            // Voice recording bar (slice 5d-4b) — replaces the input while capturing.
            <View style={[styles.composer, mobileComposer && !v3 && mStyles.composer]} testID="chat-recording-bar">
              <Pressable
                style={[styles.attachBtn, hs?.square]}
                onPress={() => stopRecording(true)}
                accessibilityLabel="Отменить запись"
                {...(typeof document !== 'undefined' ? ({ title: 'Отменить' } as object) : {})}
              >
                <Ionicons name="trash-outline" size={22} color={tc.error} />
              </Pressable>
              <View style={styles.recBar}>
                <View style={styles.recDot} />
                <Text style={styles.recTime}>{fmtDuration(recordMs)}</Text>
                <Text style={styles.recHint}>Запись…</Text>
              </View>
              <Hit
                hit={hs?.round ?? null}
                testID="chat-voice-stop-button"
                visual={[styles.sendBtn, mobileComposer && mStyles.sendBtn]}
                onPress={() => stopRecording(false)}
                accessibilityLabel="Остановить и прикрепить"
                {...(typeof document !== 'undefined' ? ({ title: 'Остановить' } as object) : {})}
              >
                <Ionicons name="checkmark" size={20} color="#fff" />
              </Hit>
            </View>
          ) : (
          <View
            testID={v3 || appearance === 'home' ? 'chat-composer' : undefined}
            style={[styles.composer, mobileComposer && !v3 && mStyles.composer]}
            // CORE-1791 (2b) — контур поля v3: .65, при фокусе внутри — 1.
            {...(v3 ? ({ onFocus: () => setComposerFocus(true), onBlur: () => setComposerFocus(false) } as object) : {})}
          >
            {v3 && <ContourRing radius={v3.g.composerRadius} opacity={composerFocus ? CONTOUR_OPACITY.composerFocus : CONTOUR_OPACITY.composer} />}
            <ComposerField pill={mobileComposer && !v3} style={mStyles.field} rowStyle={mStyles.fieldRow} header={stagedStrip}>
            {isWeb && onUpload && (
              <>
                {/* Hidden native file picker (web-only, RNW → react-dom). */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={acceptFileTypes}
                  style={{ display: 'none' }}
                  onChange={onFilesPicked}
                />
                {v3 ? (
                  <V3Button
                    visual={styles.attachBtn}
                    hover={styles.iconBtnHover}
                    still={v3.reduceMotion}
                    onPress={() => fileInputRef.current?.click?.()}
                    accessibilityRole="button"
                    accessibilityLabel="Прикрепить файл"
                    testID="chat-attach-button"
                    {...(typeof document !== 'undefined'
                      ? ({ title: attachmentHint ? `Прикрепить файл. ${attachmentHint}` : 'Прикрепить файл' } as object)
                      : {})}
                  >
                    <MessagesIcon name="attach" size={v3.g.composerButtonGlyph} color={v3.t.accent} />
                  </V3Button>
                ) : (
                <Pressable
                  style={[styles.attachBtn, mobileComposer && mStyles.fieldBtn, hs?.square]}
                  onPress={() => fileInputRef.current?.click?.()}
                  accessibilityLabel="Прикрепить файл"
                  testID="chat-attach-button"
                  {...(typeof document !== 'undefined'
                    ? ({ title: attachmentHint ? `Прикрепить файл. ${attachmentHint}` : 'Прикрепить файл' } as object)
                    : {})}
                >
                  <Ionicons name="attach" size={22} color={tc.textSecondary} />
                </Pressable>
                )}
              </>
            )}
            {(!mobileComposer || !!v3) && emojiButton}
            {draftFieldComposer ? (
              // CORE-1730 — на телефонной ширине черновик показывает эмодзи
              // комплекта картинками; наружу отдаёт тот же текст с токенами.
              <DraftField
                ref={inputRef as unknown as React.Ref<DraftFieldHandle>}
                testID="chat-composer-input"
                value={input}
                onChangeText={(t) => {
                  setInput(t);
                  if (t) onTyping?.();
                }}
                onSubmit={() => { if (canSend) submit(input); }}
                placeholder={v3 ? (placeholder ?? tChats('chat.messagePlaceholder')) : (placeholder ?? 'Сообщение')}
                placeholderColor={v3 ? v3.t.muted : tc.textMuted}
                color={v3 ? v3.t.text : tc.textPrimary}
                leading={!(isWeb && onUpload)}
                // CORE-1791 — и на desktop 44 (отступы 10/12): композер в покое 72, подсказка на оси кнопок.
                minHeight={v3 ? 44 : MOBILE_INPUT_MIN_HEIGHT}
                maxHeight={v3 ? v3.g.draftMaxHeight : MOBILE_INPUT_MAX_HEIGHT}
                // CORE-1791 (2b) — в разделе сообщений картинки в черновике 28, как в макете.
                glyphSize={v3 ? V3_EMOJI_INLINE : undefined}
                padding={v3 ? `${v3.g.draftPad.v}px ${v3.g.draftPad.h}px` : undefined}
              />
            ) : (
            <TextInput
              ref={inputRef}
              testID="chat-composer-input"
              style={[
                styles.composerInput,
                mobileComposer && !v3 && mStyles.fieldInput,
                mobileComposer && !v3 && !(isWeb && onUpload) && mStyles.fieldInputLeading,
              ]}
              value={input}
              onChangeText={(t) => {
                setInput(t);
                if (t) onTyping?.();
              }}
              placeholder={placeholder ?? (v3 ? tChats('chat.messagePlaceholder') : (mobileComposer ? 'Сообщение' : 'Ваше сообщение'))}
              placeholderTextColor={v3 ? v3.t.muted : tc.textMuted}
              multiline
              // A bare <textarea> is two rows tall; on mobile web it starts as one
              // (`rows` is RNW-only, absent from the RN TextInput types).
              {...(mobileComposer ? ({ rows: 1 } as object) : {})}
              blurOnSubmit={false}
              // Enter sends; Shift+Enter inserts a newline (standard chat behavior).
              onKeyPress={(e: any) => {
                const ne = e?.nativeEvent;
                if (ne?.key === 'Enter' && !ne.shiftKey) {
                  e.preventDefault?.();
                  // CORE-1682 (QA 16.10) — Enter шёл мимо `canSend`: с ошибочным
                  // вложением в очереди текст улетал без файла и выглядел
                  // отправленным. Клавиша подчиняется тому же правилу, что кнопка.
                  if (canSend) submit(input);
                }
              }}
            />
            )}
            {mobileComposer && !v3 && emojiButton}
            </ComposerField>
            {v3 ? (
              <>
                {/* CORE-1791 (2b) — макет: на desktop микрофон и отправка рядом, на
                    телефоне — микрофон при пустом черновике, отправка при набранном.
                    Голосовое по-прежнему только из пустого черновика (как было). */}
                {canRecord && !editing && !input.trim() && !pending.length && (
                  <V3Button
                    visual={styles.attachBtn}
                    hover={styles.iconBtnHover}
                    still={v3.reduceMotion}
                    onPress={startRecording}
                    accessibilityRole="button"
                    accessibilityLabel="Записать голосовое"
                    testID="chat-voice-button"
                    {...(typeof document !== 'undefined' ? ({ title: 'Записать голосовое' } as object) : {})}
                  >
                    <MessagesIcon name="mic" size={v3.g.composerButtonGlyph} color={v3.t.accent} />
                  </V3Button>
                )}
                {(!v3.phone || !(canRecord && !editing && !input.trim() && !pending.length)) && (
                  <V3Button
                    visual={[styles.sendBtn, !canSend && styles.sendBtnDisabled]}
                    hover={styles.sendBtnHover}
                    still={v3.reduceMotion}
                    testID="chat-send-button"
                    onPress={() => submit(input)}
                    disabled={!canSend}
                    accessibilityRole="button"
                    accessibilityLabel={editing ? 'Сохранить' : sendLabel}
                    accessibilityState={{ disabled: !canSend }}
                    {...(typeof document !== 'undefined' ? ({ title: editing ? 'Сохранить' : sendHint } as object) : {})}
                  >
                    {editing
                      ? <Ionicons name="checkmark" size={20} color={SEND_GLYPH} />
                      : <MessagesIcon name="send" size={22} color={SEND_GLYPH} />}
                  </V3Button>
                )}
              </>
            ) : canRecord && !editing && !input.trim() && !pending.length ? (
              // Empty composer → offer a voice recording (Telegram-style).
              <Hit
                hit={hs?.round ?? null}
                testID="chat-voice-button"
                visual={[styles.sendBtn, mobileComposer && mStyles.sendBtn]}
                onPress={startRecording}
                accessibilityLabel="Записать голосовое"
                {...(typeof document !== 'undefined' ? ({ title: 'Записать голосовое' } as object) : {})}
              >
                <Ionicons name="mic" size={18} color="#fff" />
              </Hit>
            ) : (
              <Hit
                hit={hs?.round ?? null}
                testID="chat-send-button"
                visual={[styles.sendBtn, mobileComposer && mStyles.sendBtn, !canSend && styles.sendBtnDisabled]}
                onPress={() => submit(input)}
                disabled={!canSend}
                accessibilityLabel={editing ? 'Сохранить' : sendLabel}
                // Native HTML hover tooltip on web (RNW forwards `title` to the DOM node).
                {...(typeof document !== 'undefined' ? ({ title: editing ? 'Сохранить' : sendHint } as object) : {})}
              >
                <Ionicons name={editing ? 'checkmark' : appearance === 'home' ? 'arrow-up' : 'send'} size={18} color={appearance === 'home' ? '#3D352B' : '#fff'} />
              </Hit>
            )}
          </View>
          )}
        </ComposeWrap>
      )}
      {!!viewer && (
        <Modal visible transparent animationType="fade" onRequestClose={closeImage}>
          <Pressable style={styles.viewerBackdrop} onPress={closeImage} accessibilityLabel="Закрыть" testID="chat-image-viewer">
            <Image source={{ uri: viewer.url }} style={styles.viewerImage} resizeMode="contain" />
            <View style={styles.viewerBar}>
              {!!viewer.name && <Text style={styles.viewerName} numberOfLines={1}>{viewer.name}</Text>}
              {isWeb && (
                <a
                  href={viewer.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: '#fff', textDecoration: 'underline', fontSize: 13 }}
                  onClick={(e: any) => e.stopPropagation?.()}
                >
                  Открыть в новой вкладке
                </a>
              )}
            </View>
            <Pressable style={[styles.viewerClose, hs?.viewerClose]} onPress={closeImage} accessibilityLabel="Закрыть" hitSlop={8}>
              <Ionicons name="close" size={24} color="#fff" />
            </Pressable>
          </Pressable>
        </Modal>
      )}
    </View>
  );
};

/**
 * Theme-aware stylesheet (CORE-396 dark-mode fix). Layout is fixed; every
 * surface / text / border colour comes from the resolved theme palette so the
 * thread matches the rest of the chat sidebar in both light and dark. Memoized
 * by the caller on `(tc, isDark)`.
 */
const makeStyles = (tc: ThemeColors, isDark: boolean) => {
  // Subtle fills that have no direct palette token — derived from the theme so
  // they read correctly on either background.
  const chipFill = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)';
  const theirsFill = isWeb ? (isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)') : tc.surfaceWarm;
  return StyleSheet.create({
    root: { flex: 1, backgroundColor: tc.surfacePrimary },
    backHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderBottomWidth: 1,
      borderBottomColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
      backgroundColor: tc.surfacePrimary,
    },
    backHeaderBtn: { padding: 2 },
    backHeaderAvatar: { width: 38, height: 38, borderRadius: 19 },
    backHeaderName: { ...uiFont(500, 700), fontSize: 16, fontWeight: '700' as const, color: tc.textPrimary },
    backHeaderSub: { ...uiFont(), fontSize: 12, marginTop: 1, color: tc.textMuted },
    center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: spacing.lg },
    errorText: { color: tc.textMuted, marginTop: spacing.md },
    // CORE-1350 (П3) — строка отказа отправки над композером.
    sendNotice: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: spacing.xs,
      paddingHorizontal: spacing.md,
      paddingBottom: spacing.xs,
    },
    sendNoticeText: { ...uiFont(), flex: 1, color: tc.error, fontSize: 13, lineHeight: 18 },
    list: { flex: 1 },
    listContent: { padding: spacing.md, gap: spacing.sm },
    // Full width so `justifyContent` reliably pushes own messages right and
    // incoming messages left (CORE-396).
    row: { flexDirection: 'row', alignItems: 'flex-end', marginBottom: spacing.sm, width: '100%' },
    rowMine: { justifyContent: 'flex-end' },
    rowTheirs: { justifyContent: 'flex-start' },
    avatar: {
      width: MESSAGE_AVATAR_SIZE,
      height: MESSAGE_AVATAR_SIZE,
      borderRadius: MESSAGE_AVATAR_SIZE / 2,
      marginRight: spacing.xs,
    },
    avatarFallback: { backgroundColor: tc.solar, alignItems: 'center', justifyContent: 'center' },
    avatarLetter: { ...uiFont(500, 600), color: '#fff', fontWeight: '600', fontSize: 13 },
    bubble: { maxWidth: '72%', paddingVertical: spacing.sm, paddingHorizontal: spacing.md, borderRadius: radius.card },
    groupBubble: { maxWidth: '88%' },
    // CORE-1730 — чужой пузырь на вебе: surfaceWarm почти сливается с фоном
    // ленты в обеих темах (на телефоне в тёмной его не видно вовсе).
    // Полупрозрачная подложка видна на любом фоне и на свечениях; натив
    // остаётся на surfaceWarm.
    bubbleTheirs: { backgroundColor: theirsFill, borderTopLeftRadius: 4 },
    bubbleMine: { backgroundColor: tc.solar, borderTopRightRadius: 4 },
    // CORE-1730 — сообщение из одних фирменных эмодзи: без заливки, рамки и
    // отступов пузыря; картинки и строка времени стоят на фоне ленты.
    bubbleEmojiOnly: { backgroundColor: 'transparent', paddingVertical: 0, paddingHorizontal: 0 },
    bubbleText: { ...uiFont(), color: tc.textPrimary, fontSize: 16, lineHeight: 24 },
    bubbleTextMine: { color: '#fff' },
    link: { color: tc.solar, textDecorationLine: 'underline' as const },
    linkMine: { color: '#fff', textDecorationLine: 'underline' as const },
    groupSenderRow: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 6,
      marginBottom: 5,
    },
    groupSender: { ...uiFont(500, 600), flexGrow: 1, flexShrink: 1, minWidth: 80, fontSize: 12, fontWeight: '600', color: tc.solar },
    groupSenderBadge: { flexShrink: 0 },
    msgWrap: { width: '100%' },
    cardsWrap: { marginTop: 4, marginLeft: 40, marginRight: spacing.md },
    alignSelfEnd: { alignSelf: 'flex-end' },
    quoted: {
      borderLeftWidth: 3, borderLeftColor: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.5)',
      paddingLeft: spacing.sm, marginBottom: spacing.xs, opacity: 0.85,
    },
    quotedSender: { ...uiFont(500, 600), fontSize: 12, fontWeight: '600', color: tc.textPrimary },
    quotedText: { ...uiFont(), fontSize: 13, color: tc.textSecondary },
    // CORE-1683 — the deleted-original note: muted on a neutral bubble, white
    // on an own (brand-orange) one, where muted grey all but vanished.
    quotedDeleted: { color: tc.textMuted },
    quotedDeletedMine: { color: 'rgba(255,255,255,0.85)' },
    // CORE-1730 — цитата в сообщении без пузыря: своя мягкая подложка, иначе
    // её белая полоска на фоне ленты исчезает.
    quotedEmojiOnly: {
      borderLeftColor: tc.solar, backgroundColor: theirsFill, opacity: 1,
      paddingVertical: spacing.xs, paddingRight: spacing.sm, marginBottom: spacing.sm,
      borderTopRightRadius: radius.sm, borderBottomRightRadius: radius.sm,
    },
    // CORE-1683 — the jump target: a wash behind the whole row (an own bubble is
    // already brand-orange, so a ring on it would not show), gone in a moment.
    msgWrapHighlighted: {
      backgroundColor: isDark ? 'rgba(255, 184, 77, 0.18)' : 'rgba(255, 184, 77, 0.22)',
      borderRadius: radius.lg,
    },
    attachImage: { width: 220, height: 160, borderRadius: radius.lg, marginBottom: spacing.xs, backgroundColor: tc.surfaceWarm },
    attachChip: {
      flexDirection: 'row', alignItems: 'center', gap: 6, maxWidth: 240,
      paddingVertical: 6, paddingHorizontal: 10, marginBottom: spacing.xs,
      borderRadius: radius.pill, backgroundColor: chipFill,
    },
    attachChipText: { ...uiFont(), fontSize: 14, color: tc.textPrimary, flexShrink: 1 },
    videoPoster: { position: 'relative', marginBottom: spacing.xs, alignItems: 'center', justifyContent: 'center' },
    videoFallback: { alignItems: 'center', justifyContent: 'center' },
    playBadge: {
      position: 'absolute', width: 44, height: 44, borderRadius: 22,
      backgroundColor: 'rgba(0,0,0,0.55)', alignItems: 'center', justifyContent: 'center',
    },
    receipt: { ...uiFont(), fontSize: 12, lineHeight: 16, color: 'rgba(255,255,255,0.7)' },
    metaRow: { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-end', gap: 5, marginTop: 2 },
    msgTime: { ...uiFont(), fontSize: 12, lineHeight: 16 },
    msgTimeMine: { color: 'rgba(255,255,255,0.7)' },
    msgTimeTheirs: { color: tc.textMuted },
    // CORE-1730 — под эмодзи без пузыря: чужое время слева, свои галочки акцентом.
    metaRowStart: { alignSelf: 'flex-start' },
    receiptEmojiOnly: { color: tc.solar },
    dateSep: { alignItems: 'center', paddingVertical: 10 },
    dateSepText: {
      fontSize: 12, fontWeight: '700', letterSpacing: 0.6,
      color: tc.textMuted, backgroundColor: 'rgba(128,128,128,0.14)',
      paddingHorizontal: 12, paddingVertical: 5, borderRadius: 999, overflow: 'hidden',
    },
    reactionsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 4, marginHorizontal: spacing.md },
    reactionsTheirs: { marginLeft: 40 },
    reactionChip: {
      flexDirection: 'row', alignItems: 'center', gap: 3,
      backgroundColor: tc.surfaceWarm, borderRadius: radius.pill,
      paddingVertical: 2, paddingHorizontal: 8, borderWidth: 1, borderColor: 'transparent',
    },
    reactionChipMine: { borderColor: tc.solar, backgroundColor: 'rgba(233, 130, 69,0.08)' },
    reactionEmoji: { fontSize: 14 },
    reactionCount: { ...uiFont(), fontSize: 12, color: tc.textSecondary },
    // CORE-696 — the menu is a card now: the reaction strip on top, then the
    // actions as labelled rows, so an open menu says what it offers.
    menuCard: {
      marginTop: 4, marginHorizontal: spacing.md, alignSelf: 'flex-start', minWidth: 196,
      backgroundColor: tc.surfaceElevated, borderRadius: radius.card,
      borderWidth: 1, borderColor: tc.border, paddingVertical: spacing.xs, overflow: 'hidden',
    },
    menuItem: {
      flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
      paddingVertical: spacing.sm, paddingHorizontal: spacing.md,
    },
    menuLabel: { ...uiFont(), color: tc.textPrimary, fontSize: 16, lineHeight: 24 },
    menuLabelDanger: { color: tc.error },
    // CORE-697 — the pin: a banner over the thread and a mark on the message
    // itself, so the pin is findable from the top and recognisable in place.
    pinnedBar: {
      flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
      paddingVertical: spacing.xs, paddingHorizontal: spacing.md,
      borderBottomWidth: 1, borderBottomColor: tc.border, backgroundColor: tc.surfaceWarm,
    },
    pinnedBarTitle: { ...uiFont(500, 700), color: tc.solar, fontSize: 12, lineHeight: 16, fontWeight: '700' },
    pinnedBarText: { ...uiFont(), color: tc.textSecondary, fontSize: 13 },
    pinnedChip: {
      flexDirection: 'row', alignItems: 'center', gap: 4,
      marginHorizontal: spacing.md, marginBottom: 2, alignSelf: 'flex-start',
    },
    pinnedChipText: { ...uiFont(500, 700), color: tc.solar, fontSize: 12, lineHeight: 16, fontWeight: '700' },
    picker: {
      flexDirection: 'row', gap: spacing.xs, alignSelf: 'flex-start',
      paddingVertical: 4, paddingHorizontal: spacing.sm,
      borderBottomWidth: 1, borderBottomColor: tc.border, width: '100%',
    },
    // CORE-696 — the web affordance for the message menu: quiet at rest so a
    // long thread doesn't turn into a wall of dots, solid once you reach for it.
    menuBtn: { paddingHorizontal: 4, paddingVertical: 2, alignSelf: 'flex-end', marginBottom: 2, opacity: 0.45 },
    menuBtnActive: { opacity: 1 },
    pickerItem: { paddingHorizontal: 4, alignItems: 'center', justifyContent: 'center' },
    pickerEmoji: { fontSize: 20 },
    pickerItemMine: { backgroundColor: 'rgba(233, 130, 69,0.16)', borderRadius: radius.md },
    // CORE-1685 — палитра комплекта у поля ввода.
    emojiBtn: {
      width: 36, height: 36, alignItems: 'center', justifyContent: 'center',
      ...(Platform.OS === 'web' ? { cursor: 'pointer' as any } : {}),
    },
    thinkingText: { ...uiFont(), color: tc.textMuted, fontSize: 14 },
    quickReplies: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, paddingHorizontal: spacing.md, paddingBottom: spacing.sm },
    chip: { borderWidth: 1, borderColor: tc.solar, borderRadius: radius.pill, paddingVertical: 6, paddingHorizontal: spacing.md },
    chipText: { ...uiFont(), color: tc.solar, fontSize: 14 },
    mentionMenu: {
      marginHorizontal: spacing.md, marginBottom: spacing.xs, borderRadius: radius.card,
      backgroundColor: tc.surfaceElevated, borderWidth: 1, borderColor: tc.border,
      overflow: 'hidden',
    },
    mentionItem: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm, paddingHorizontal: spacing.md },
    mentionAvatar: { marginRight: 0 },
    mentionName: { ...uiFont(), color: tc.textPrimary, fontSize: 16, lineHeight: 24, flexShrink: 1 },
    mentionMeta: { ...uiFont(), color: tc.textMuted, fontSize: 13, lineHeight: 20, marginLeft: 'auto' },
    mentionEmpty: { ...uiFont(), color: tc.textMuted, fontSize: 14, lineHeight: 20, paddingVertical: spacing.sm, paddingHorizontal: spacing.md },
    mention: { ...uiFont(500, 600), color: tc.solar, fontWeight: '600' },
    mentionMine: { color: '#FFFFFF', textDecorationLine: 'underline' },
    mentionSelf: { backgroundColor: 'rgba(232,93,4,0.18)', borderRadius: 4 },
    editBanner: {
      flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
      paddingHorizontal: spacing.md, paddingVertical: spacing.xs,
      borderTopWidth: 1, borderTopColor: tc.border,
      backgroundColor: 'rgba(233, 130, 69,0.06)',
    },
    editBannerText: { ...uiFont(), flex: 1, fontSize: 13, color: tc.solar },
    replyBannerBody: { flex: 1 },
    replyBannerName: { ...uiFont(500, 600), fontSize: 12, fontWeight: '600', color: tc.solar },
    replyBannerText: { ...uiFont(), fontSize: 13, color: tc.textSecondary },
    mutedBar: {
      flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
      padding: spacing.md, borderTopWidth: 1, borderTopColor: tc.border,
    },
    mutedText: { ...uiFont(), flex: 1, fontSize: 14, color: tc.textSecondary },
    composer: {
      flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
      padding: spacing.md, borderTopWidth: 1, borderTopColor: tc.border,
    },
    composerInput: {
      flex: 1, maxHeight: 120, minHeight: 40, backgroundColor: tc.surfaceWarm,
      borderRadius: radius.card, paddingHorizontal: spacing.md, paddingVertical: 8,
      // 40px box (border-box) − 24px line = 8px top/bottom → single line sits
      // vertically centred, aligned with the 40px attach / send buttons. The
      // padding follows the line-height: input.default is 16/24, and 16px is
      // also the floor below which mobile Safari zooms the page on focus.
      ...uiFont(),
      lineHeight: 24, fontSize: 16, color: tc.textPrimary,
    },
    sendBtn: {
      width: 40, height: 40, borderRadius: 20, backgroundColor: tc.solar,
      alignItems: 'center', justifyContent: 'center',
      ...(Platform.OS === 'web' ? {
        backgroundImage: `linear-gradient(135deg, ${tc.ctaStart}, ${tc.ctaEnd})`,
        boxShadow: tc.ctaShadow,
        cursor: 'pointer',
      } as any : { elevation: 7 }),
    },
    sendBtnDisabled: { opacity: 0.4 },
    attachBtn: {
      width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center',
      ...(Platform.OS === 'web' ? { cursor: 'pointer' as any } : {}),
    },
    // Staged attachment previews above the composer (slice 5d-4).
    stagedStrip: {
      flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm,
      paddingHorizontal: spacing.md, paddingTop: spacing.sm,
    },
    stagedItem: { width: 64, height: 64, borderRadius: radius.md, overflow: 'visible' },
    stagedThumb: { width: 64, height: 64, borderRadius: radius.md, backgroundColor: tc.surfaceWarm },
    stagedFile: {
      alignItems: 'center', justifyContent: 'center', paddingHorizontal: 4,
      borderWidth: 1, borderColor: tc.border,
    },
    stagedName: { ...uiFont(), fontSize: 12, lineHeight: 16, color: tc.textSecondary, marginTop: 2, maxWidth: 58 },
    stagedOverlay: {
      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
      borderRadius: radius.md, backgroundColor: 'rgba(0,0,0,0.45)',
      alignItems: 'center', justifyContent: 'center',
    },
    stagedErrorOverlay: { backgroundColor: 'rgba(214,69,69,0.65)' },
    // CORE-1682 — retry sits on the failed chip itself; the limits read under the strip.
    stagedRetryText: { ...uiFont(500, 600), fontSize: 12, lineHeight: 16, color: '#fff', marginTop: 2 },
    attachHint: {
      ...uiFont(), fontSize: 12, lineHeight: 16, color: tc.textMuted,
      paddingHorizontal: spacing.md, paddingBottom: spacing.xs,
    },
    // CORE-1682 — почему кнопка и Enter молчат: файл с ошибкой держит отправку.
    attachBlocked: {
      ...uiFont(), fontSize: 12, lineHeight: 16, color: '#D9534F',
      paddingHorizontal: spacing.md, paddingBottom: spacing.xs,
    },
    viewerBackdrop: {
      flex: 1, backgroundColor: 'rgba(0,0,0,0.88)', alignItems: 'center', justifyContent: 'center', padding: spacing.lg,
    },
    viewerImage: { width: '100%', height: '80%' },
    viewerBar: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, marginTop: spacing.sm, maxWidth: '100%' },
    viewerName: { ...uiFont(), color: '#fff', fontSize: 13, flexShrink: 1 },
    viewerClose: {
      position: 'absolute', top: spacing.lg, right: spacing.lg, width: 40, height: 40, borderRadius: 20,
      backgroundColor: 'rgba(255,255,255,0.15)', alignItems: 'center', justifyContent: 'center',
    },
    stagedRemove: {
      position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: 9,
      backgroundColor: tc.charcoal, alignItems: 'center', justifyContent: 'center',
      ...(Platform.OS === 'web' ? { cursor: 'pointer' as any } : {}),
    },
    // Voice recording bar (slice 5d-4b).
    recBar: {
      flex: 1, flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
      minHeight: 40, paddingHorizontal: spacing.md,
      backgroundColor: tc.surfaceWarm, borderRadius: radius.card,
    },
    recDot: { width: 10, height: 10, borderRadius: 5, backgroundColor: tc.error },
    recTime: { ...uiFont(), fontSize: 16, lineHeight: 24, color: tc.textPrimary, fontVariant: ['tabular-nums'] },
    recHint: { ...uiFont(), fontSize: 14, color: tc.textMuted },
  });
};
